/**
 * AlphaX Tuning Simulator | Copyright (C) 2026 AlphaX
 * Licensed under GPL-3.0 (https://www.gnu.org/licenses/gpl-3.0.txt)
 * Subscriptions do not negate this license; derivative works must be open source.
 */
/**
 * AlphaX Tuning Simulator - Physics & Simulation Engine
 */

class AlphaXPhysics {
    constructor(engine, ecu) {
        this.engine = engine; 
        this.ecu = ecu;
        this.engineBlown = false; 
        this.stallTimer = 0;
        this.overrunTimer = 0;
        this.triggerPopRevAccum      = 0; // accumulated crank degrees — used for per-revolution pop gate
        this.triggerTimingStallTimer = 0; // stall countdown when trigger error is too far off
        this.activeMods = [];
        this.activePumpLph = null;           // null = use ecu.stockFuelPumpLph; set by loadMods
        this.installedInjectorSizeCc = null;  // null = match ECU programmed size; set by loadMods
        this.installedInjectorDtMs   = null;  // null = use ECU deadtime curve; set by loadMods
        this.clutchTorqueRating = 300;  // effective lb-ft — recomputed in loadMods
        this.clutchHeatCapJ    = 55000; // effective Joules — recomputed in loadMods
        this.actualBoostKpa = 0;
        this.effectiveWgDuty = 0;  // first-order lag state (τ=0.3s)
        this.clBoostIntegral = 0;  // closed-loop boost PID integral accumulator
        this.clBoostPrevErr  = 0;  // closed-loop boost PID previous error
        this.turboSpeedRpm = 0;   // dynamic turbo shaft speed (state, not a map lookup)
        this.turboHeatAccumulatedC = 0; // accumulated heat from turbo beyond max efficiency
        this.scHeatAccumulatedC = 0;    // accumulated heat from supercharger (heat soak)
        this.lastTorque = 0;      // previous frame's output torque — drives the turbine
        this.afrNoiseCurrent = 0; // smoothed combustion variance noise (fraction)
        this.afrNoiseTarget  = 0; // target noise fraction
        this.scBoostKpa = 0;      // current supercharger boost (computed each frame)
        this.actualVvtIntake  = 0; // persistent actual cam position (PID-controlled)
        this.actualVvtExhaust = 0;
        this.vvtIntIn  = 0;  // intake PID integral accumulator
        this.vvtErrIn  = 0;  // intake PID previous error
        this.vvtIntEx  = 0;  // exhaust PID integral accumulator
        this.vvtErrEx  = 0;  // exhaust PID previous error
        this.knockCount = 0;
        this.knockCascadeNextTier = null; // which tier gets 2x probability boost next frame
        this.knockCascadeFrames   = 0;    // frames remaining in current cascade
        this.revLimitActive = false;
        this.stft = 0;             // short-term fuel trim fraction (e.g. 0.05 = +5% fuel)
        this._prevActualAfr = 14.7;
        this.lcActive = false; // Track LC hysteresis state
        this.boostCutActive = false; // Track boost cut hysteresis state
        this.lopePhase = 0;
        this._prevLopeOffset = 0;

        // Acceleration enrichment state
        this.filteredMapKpa  = -1;  // MAP lag filter state (-1 = uninitialised)
        this.accelCurrentMs  = 0;   // live ms adder remaining (ms_adder mode)
        this.accelBlend      = 0;   // MAP prediction blend 0→1 (map_predict mode)
        this.accelTimer      = 0;   // seconds elapsed since last trigger

        this.precomputeEngineMaps();
    }
    
    loadMods(modsArray, isTwinTurbo = false) {
        this.activeMods = modsArray;
        this.isTwinTurbo = isTwinTurbo;

        let hasTurbo = false;
        for (let m of modsArray) {
            if (m.mod_type === "Turbocharger") hasTurbo = true;
        }
        if (this.ecu && typeof this.ecu.updateTurboAxes === 'function') {
            this.ecu.updateTurboAxes(hasTurbo);
        }

        // Compute effective clutch specs from engine OEM base × Clutch mod multipliers
        const limits = (typeof sampleEngine !== 'undefined') ? sampleEngine?.mechanical_limits : null;
        const oemTq   = limits?.oem_clutch_torque_lbft ?? 300;
        const oemHeat = limits?.oem_clutch_heat_kj     ?? 110;
        let tqMult   = 1.0;
        let heatMult = 1.0;
        for (let m of modsArray) {
            if (m.mod_type === "Clutch" && m.type_specific_parameters) {
                tqMult   *= m.type_specific_parameters.torque_capacity_multiplier ?? 1.0;
                heatMult *= m.type_specific_parameters.heat_capacity_multiplier   ?? 1.0;
            }
        }
        this.clutchTorqueRating = oemTq * tqMult;
        this.clutchHeatCapJ     = oemHeat * 1000 * heatMult;

        // Fuel pump upgrade
        const pumpMod = modsArray.find(m => m.mod_type === "FuelPump");
        this.activePumpLph = pumpMod ? pumpMod.type_specific_parameters.flow_lph : null;

        // Injector upgrade
        const injectorMod = modsArray.find(m => m.mod_type === "Injector");
        this.installedInjectorSizeCc = injectorMod
            ? (injectorMod.type_specific_parameters?.flow_cc_per_min ?? null)
            : null;
        this.installedInjectorDtMs = injectorMod
            ? (injectorMod.type_specific_parameters?.dead_time_ms ?? null)
            : null;

        if (typeof renderAllTables === 'function') renderAllTables();
    }

    precomputeEngineMaps() {
        this.mapCache = {};
        let peakHp = 0;
        for (const mapName in this.engine.performance_maps) {
            const mapData = this.engine.performance_maps[mapName];
            const rpmKeys = Object.keys(mapData).map(Number).sort((a, b) => a - b);
            this.mapCache[mapName] = {
                rpms: rpmKeys,
                tqs: rpmKeys.map(k => mapData[k].base_torque),
                ves: rpmKeys.map(k => mapData[k].ideal_ve),
                afrs: rpmKeys.map(k => mapData[k].ideal_afr || 14.7),
                mbts: rpmKeys.map(k => mapData[k].ideal_timing_mbt)
            };

            // Calculate peak HP anchor from the base map to define "engine size" for blowers
            if (mapName === "Base" || mapName === "Low Cam Profile") {
                rpmKeys.forEach(rpm => {
                    const hp = (mapData[rpm].base_torque * rpm) / 5252;
                    if (hp > peakHp) peakHp = hp;
                });
            }
        }
        this.maxNaHp = peakHp || 100; // Static anchor for blower scaling
    }

    getMapDataAtRpm(mapName, rpm) {
        const cache = this.mapCache[mapName];
        if (!cache) return null;
        return {
            torque: Interpolation.interpolate1D(rpm, cache.rpms, cache.tqs, true),
            ve: Interpolation.interpolate1D(rpm, cache.rpms, cache.ves),
            afr: Interpolation.interpolate1D(rpm, cache.rpms, cache.afrs),
            timing: Interpolation.interpolate1D(rpm, cache.rpms, cache.mbts)
        };
    }

    interpolateCamAngle(angle, prefix, rpm) {
        const step = 10;
        const lowerAngle = Math.floor(angle / step) * step;
        const upperAngle = Math.ceil(angle / step) * step;

        const lowerMapName = lowerAngle === 0 ? "Base" : `${prefix}_${lowerAngle}`;
        const upperMapName = `${prefix}_${upperAngle}`;

        const lowerData = this.getMapDataAtRpm(lowerMapName, rpm);
        const upperData = this.getMapDataAtRpm(upperMapName, rpm);

        if (!lowerData && !upperData) return null;
        if (!upperData) return lowerData;
        if (!lowerData) return upperData;
        if (lowerAngle === upperAngle) return lowerData;

        const t = (angle - lowerAngle) / (upperAngle - lowerAngle);

        return {
            torque: lowerData.torque + t * (upperData.torque - lowerData.torque),
            ve: lowerData.ve + t * (upperData.ve - lowerData.ve),
            afr: lowerData.afr + t * (upperData.afr - lowerData.afr),
            timing: lowerData.timing + t * (upperData.timing - lowerData.timing)
        };
    }

    getMaxVvtAngle(prefix) {
        let max = 0;
        for (const key of Object.keys(this.mapCache)) {
            const m = key.match(new RegExp(`^${prefix}_(\\d+)$`));
            if (m) max = Math.max(max, parseInt(m[1]));
        }
        return max;
    }

    // PID step for VVT phaser. Output is velocity (deg/s) applied to actual position.
    _vvtPidStep(target, actual, integral, prevErr, kp, ki, kd, dt) {
        const err = target - actual;
        const newIntegral = integral + err * dt;
        const deriv = dt > 0 ? (err - prevErr) / dt : 0;
        const output = kp * err + ki * newIntegral + kd * deriv;
        return { output, integral: newIntegral, prevErr: err };
    }

    calculateEngineVVTState(rpm, intakeAngle, exhaustAngle, vvlActive, vvtDelete) {
        const mode = this.engine.vvt_parameters?.vvt_mode || "None";
        
        // Establish the baseline map name (Fallback to Low Cam Profile if Base doesn't exist)
        let baseMapName = "Base";
        if (!this.mapCache["Base"] && this.mapCache["Low Cam Profile"]) {
            baseMapName = "Low Cam Profile";
        }

        // Base map fallback or VVT Delete from a mod
        if (vvtDelete || (mode === "None" && !this.mapCache["High Cam Profile"])) {
            return this.getMapDataAtRpm(baseMapName, rpm) || { torque: 0, ve: 0, afr: 14.7, timing: 0 };
        }

        // Pure VTEC Engine (No VTC)
        if (mode === "OnOff") {
            if (vvlActive && this.mapCache["High Cam Profile"]) {
                return this.getMapDataAtRpm("High Cam Profile", rpm);
            }
            return this.getMapDataAtRpm("Low Cam Profile", rpm) || this.getMapDataAtRpm(baseMapName, rpm) || { torque: 0, ve: 0, afr: 14.7, timing: 0 };
        }

        // Continuous VVT (Intake or Dual) + VVL Stacking (i-VTEC)
        const baseData = this.getMapDataAtRpm(baseMapName, rpm);
        if (!baseData) return { torque: 0, ve: 0, afr: 14.7, timing: 0 }; // Failsafe
        
        let finalData = { ...baseData };

        // 1. VVL (VTEC) Superposition Delta
        if (vvlActive && this.mapCache["High Cam Profile"]) {
            const highData = this.getMapDataAtRpm("High Cam Profile", rpm);
            if (highData) {
                finalData.torque += (highData.torque - baseData.torque);
                finalData.ve += (highData.ve - baseData.ve);
                finalData.afr += (highData.afr - baseData.afr);
                finalData.timing += (highData.timing - baseData.timing);
            }
        }

        // 2. VVT Intake Superposition Delta
        if ((mode === "Intake" || mode === "Dual") && intakeAngle > 0) {
            const intakeData = this.interpolateCamAngle(intakeAngle, "In", rpm);
            if (intakeData) {
                finalData.torque += (intakeData.torque - baseData.torque);
                finalData.ve += (intakeData.ve - baseData.ve);
                finalData.afr += (intakeData.afr - baseData.afr);
                finalData.timing += (intakeData.timing - baseData.timing);
            }
        }

        // 3. VVT Exhaust Superposition Delta
        if (mode === "Dual" && exhaustAngle < 0) {
            const absExhaustAngle = Math.abs(exhaustAngle);
            const exhaustData = this.interpolateCamAngle(absExhaustAngle, "Ex", rpm);
            if (exhaustData) {
                finalData.torque += (exhaustData.torque - baseData.torque);
                finalData.ve += (exhaustData.ve - baseData.ve);
                finalData.afr += (exhaustData.afr - baseData.afr);
                finalData.timing += (exhaustData.timing - baseData.timing);
            }
        }

        return finalData;
    }

    getModModifiersAtRpm(rpm) {
        let agg = {
            torque_mult: 1.0,
            ve_mult: 1.0,
            timing_adder: 0.0,
            vacuum_penalty: 0.0,
            vvt_delete: false,
            vvl_delete: false,
            iat_reduction_percent: 0.0,
            tq_capacity_adder: 0.0,
            cr_adder: 0.0,
            powerband_shift: 0,
            rpm_shift: 0,
            cam_lope_rpm: 0
        };
        let turboProfile = null;
        let scProfile = null;

        this.activeMods.forEach(mod => {
            if (mod.mod_type === "Turbocharger") {
                turboProfile = { ...mod.type_specific_parameters };
                if (this.isTwinTurbo) {
                    // Twin turbo = two identical turbos in parallel. Scale all flow/inertia parameters
                    // by 1.9x so the pair behaves like one larger turbo with the same efficiency curve.
                    // Excluded: max_turbo_rpm, spindown_rate, max_pressure_ratio,
                    //           timing_retard_per_psi, afr_adder_per_psi (per-turbo aero/fueling constants).
                    const TT = 1.9;
                    
                    // Simple model properties (used by most mod files)
                    turboProfile.spool_turbo_begin_hp = (turboProfile.spool_turbo_begin_hp || 50) * TT;
                    turboProfile.turbo_spool_onset_hp = (turboProfile.turbo_spool_onset_hp || 2.0) * TT;
                    turboProfile.max_turbo_hp         = (turboProfile.max_turbo_hp         || 500) * TT;

                    // Complex model properties (used by d16_turbo_tests and described in GEMINI.md)
                    turboProfile.spool_threshold_hp = (turboProfile.spool_threshold_hp || 50)  * TT;
                    turboProfile.turbine_inertia    = (turboProfile.turbine_inertia    || 5.0)  * TT;
                    turboProfile.max_airflow_cfm    = (turboProfile.max_airflow_cfm    || 850)  * TT;
                }
            }

            if (mod.mod_type === "Supercharger") {
                scProfile = mod.type_specific_parameters;
            }

            if (mod.mod_type === "Cooling" && mod.type_specific_parameters) {
                agg.iat_reduction_percent += (mod.type_specific_parameters.iat_reduction_percent || 0);
            }

            // Forged Rods / Pistons / Internals
            if (mod.type_specific_parameters) {
                if (mod.type_specific_parameters.tq_capacity_adder) {
                    agg.tq_capacity_adder += mod.type_specific_parameters.tq_capacity_adder;
                }
                if (mod.type_specific_parameters.cr_adder) {
                    agg.cr_adder += mod.type_specific_parameters.cr_adder;
                }
            }

            if (mod.rpm_modifiers) {
                const keys = Object.keys(mod.rpm_modifiers).map(Number).sort((a,b)=>a-b);
                const tqVals = keys.map(k => mod.rpm_modifiers[k].torque_multiplier || 1.0);
                const veVals = keys.map(k => mod.rpm_modifiers[k].ve_multiplier || 1.0);
                const timVals = keys.map(k => mod.rpm_modifiers[k].timing_adder || 0.0);

                // Multiply modifiers across all active mods
                agg.torque_mult *= Interpolation.interpolate1D(rpm, keys, tqVals);
                agg.ve_mult *= Interpolation.interpolate1D(rpm, keys, veVals);
                agg.timing_adder += Interpolation.interpolate1D(rpm, keys, timVals);
            }

            if (mod.mod_type === "Camshaft" && mod.type_specific_parameters) {
                if (mod.type_specific_parameters.idle_vacuum_kpa_penalty) {
                    agg.vacuum_penalty += mod.type_specific_parameters.idle_vacuum_kpa_penalty;
                }
                if (mod.type_specific_parameters.cam_lope_rpm) {
                    agg.cam_lope_rpm += mod.type_specific_parameters.cam_lope_rpm;
                }
                if (mod.type_specific_parameters.vvt_delete) {
                    agg.vvt_delete = true;
                }
                if (mod.type_specific_parameters.vvl_delete) {
                    agg.vvl_delete = true;
                }
                if (mod.type_specific_parameters.powerband_shift_rpm) {
                    agg.powerband_shift += mod.type_specific_parameters.powerband_shift_rpm;
                }
                if (mod.type_specific_parameters.rpm_shift) {
                    agg.rpm_shift += mod.type_specific_parameters.rpm_shift;
                }
            }

            if (mod.mod_type === "Intake Manifold" && mod.type_specific_parameters) {
                if (mod.type_specific_parameters.powerband_shift_rpm) {
                    agg.powerband_shift += mod.type_specific_parameters.powerband_shift_rpm;
                }
            }
        });

        return { ...agg, turboProfile: turboProfile, scProfile: scProfile };
    }

    /* 
    // Legacy 4D Compressor Map Lookup (Disabled in favor of Heuristic Model)
    _lookup4DCompressorMap(mapPoints, wa_lbmin, currentPR) {
        if (!mapPoints || mapPoints.length === 0)
            return { turboSpeedTarget: 0, turboEff: 65, inSurge: false, inChoke: false };

        const lineMap = {};
        for (const pt of mapPoints) {
            if (!lineMap[pt.shaft_rpm]) lineMap[pt.shaft_rpm] = [];
            lineMap[pt.shaft_rpm].push(pt);
        }
        const lines = Object.values(lineMap).sort((a, b) => a[0].shaft_rpm - b[0].shaft_rpm);
        for (const line of lines) line.sort((a, b) => a.airflow_lbmin - b.airflow_lbmin);

        function interpOnLine(line, wa) {
            if (wa <= line[0].airflow_lbmin)
                return { eff: line[0].efficiency_percent, pr: line[0].pressure_ratio, atSurge: true, atChoke: false };
            const last = line.length - 1;
            if (wa >= line[last].airflow_lbmin)
                return { eff: line[last].efficiency_percent, pr: line[last].pressure_ratio, atSurge: false, atChoke: true };
            for (let i = 0; i < last; i++) {
                if (line[i].airflow_lbmin <= wa && wa <= line[i + 1].airflow_lbmin) {
                    const t = (wa - line[i].airflow_lbmin) / (line[i + 1].airflow_lbmin - line[i].airflow_lbmin);
                    return {
                        eff: line[i].efficiency_percent + t * (line[i + 1].efficiency_percent - line[i].efficiency_percent),
                        pr:  line[i].pressure_ratio     + t * (line[i + 1].pressure_ratio     - line[i].pressure_ratio),
                        atSurge: false, atChoke: false
                    };
                }
            }
            return { eff: line[0].efficiency_percent, pr: line[0].pressure_ratio, atSurge: false, atChoke: false };
        }

        const lineData = lines.map(l => ({ rpm: l[0].shaft_rpm, ...interpOnLine(l, wa_lbmin) }));
        const prValues = lineData.map(d => d.pr);

        let turboSpeedTarget, turboEff, inSurge, inChoke;
        if (currentPR <= prValues[0]) {
            turboSpeedTarget = lineData[0].rpm * 0.8;
            turboEff = lineData[0].eff;
            inSurge  = lineData[0].atSurge;
            inChoke  = lineData[0].atChoke;
        } else if (currentPR >= prValues[prValues.length - 1]) {
            const n = prValues.length;
            const prStep  = prValues[n - 1] - prValues[n - 2];
            const rpmStep = lineData[n - 1].rpm - lineData[n - 2].rpm;
            const extraRpm = prStep > 0 ? (currentPR - prValues[n - 1]) / prStep * rpmStep : 0;
            turboSpeedTarget = lineData[n - 1].rpm + extraRpm;
            turboEff = lineData[n - 1].eff;
            inSurge  = lineData[n - 1].atSurge;
            inChoke  = lineData[n - 1].atChoke;
        } else {
            let i = 0;
            while (i < prValues.length - 1 && prValues[i + 1] < currentPR) i++;
            const t = (currentPR - prValues[i]) / (prValues[i + 1] - prValues[i]);
            turboSpeedTarget = lineData[i].rpm + t * (lineData[i + 1].rpm - lineData[i].rpm);
            turboEff         = lineData[i].eff + t * (lineData[i + 1].eff - lineData[i].eff);
            inSurge = lineData[i].atSurge || lineData[i + 1].atSurge;
            inChoke = lineData[i].atChoke || lineData[i + 1].atChoke;
        }
        return { turboSpeedTarget, turboEff: Math.max(5, Math.min(100, turboEff)), inSurge, inChoke };
    }
    */

    _updateStft(rpm, tps, prevActualAfr, targetAfr, dt) {
        // Mirrors RusEFI ClosedLoopFuelCellBase::update()
        // lambdaError > 0 = running lean → add fuel (stft increases)
        // lambdaError < 0 = running rich → remove fuel (stft decreases)
        const lambdaError = (prevActualAfr - targetAfr) / Math.max(targetAfr, 0.1);
        if (Math.abs(lambdaError) < this.ecu.clDeadband) return;

        const isIdle = tps < 5 && rpm < ((this.ecu.liveIdleTargetRpm ?? 850) + 200);
        const isWot  = tps > 90;
        let tc, min, max;
        if (isIdle) {
            tc = this.ecu.clIdleTimeConstant; min = this.ecu.clIdleMin; max = this.ecu.clIdleMax;
        } else if (isWot) {
            tc = this.ecu.clWotTimeConstant;  min = this.ecu.clWotMin;  max = this.ecu.clWotMax;
        } else {
            tc = this.ecu.clRunningTimeConstant; min = this.ecu.clRunningMin; max = this.ecu.clRunningMax;
        }
        const gain = 1 / Math.max(tc, 0.1);
        const adjust = gain * lambdaError * dt + this.stft;
        this.stft = Math.max(min, Math.min(max, adjust));
    }

    _blowEngine(reason) {
        this.engineBlown = true;
        if (!window.location.pathname.includes('tests')) {
            console.error(`[ENGINE BLOWN] 💥 ${reason}`);
        }
    }

    calculateTorqueAtRPM(rpm, tps, dt, idleTimingAdj = 0, crankDecay = 0, isCranking = false, isPedalHeldDown = true, isDynoPullActive = false, isDragStaging = false, nitrousActive = false, isFloodClear = false, dTps = 0, knockRetardDeg = 0, postCrankEnrich = 1.0) {
        if (this.engineBlown) return { error: "Engine Destroyed", valid: false };

        const modModifiers = this.getModModifiersAtRpm(rpm);

        // --- CAM LOPE ---
        // Apply as a frame-to-frame RPM delta so the sine wave becomes a true RPM oscillation.
        this.lopePhase += dt * 2 * Math.PI * 3; // 3 Hz
        let lopeRpmOffset = 0;
        if (modModifiers.cam_lope_rpm > 0 && !isCranking && !isDynoPullActive && !isDragStaging) {
            const lopeFade = Math.max(0, 1 - tps / 5);
            lopeRpmOffset = modModifiers.cam_lope_rpm * lopeFade * (1 + Math.sin(this.lopePhase)) / 2;
        }
        const lopeRpmDelta = lopeRpmOffset - this._prevLopeOffset;
        this._prevLopeOffset = lopeRpmOffset;

        const _ambientRaw = parseFloat(document.getElementById('sim-ambient-temp')?.value);
        const _ambientUnit = document.getElementById('sim-ambient-temp')?.dataset?.unit || 'F';
        const ambientTempF = isNaN(_ambientRaw) ? 80 : (_ambientUnit === 'C' ? _ambientRaw * 9 / 5 + 32 : _ambientRaw);
        const baroKpa = parseFloat(document.getElementById('sim-baro')?.value) || 101.3;

        // --- LAUNCH CONTROL DETECTION ---
        let launchFuelAdder = 0;
        let launchTimingOverride = null;
        let isLaunchLimitActive = false;
        let isLcArmed = advancedMode && this.ecu.lcEnabled && (isDragStaging || this.ecu.twoStepSwitchActive);
        let launchCutType = this.ecu.lcCutType || "Fuel";

        if (isLcArmed) {
            // 1. Hysteresis State Management
            if (rpm >= this.ecu.lcRpm) {
                this.lcActive = true;
            } else if (rpm <= (this.ecu.lcRpm - this.ecu.lcHysteresis)) {
                this.lcActive = false;
            }
            isLaunchLimitActive = this.lcActive;

            // 2. Window RPM Logic (Smooth Transitions)
            const windowStartRpm = this.ecu.lcRpm - this.ecu.lcWindowRpm;
            if (rpm > windowStartRpm) {
                const progress = Math.max(0, Math.min(1.0, (rpm - windowStartRpm) / Math.max(1, this.ecu.lcWindowRpm)));
                
                // Absolute timing transition
                launchTimingOverride = this.ecu.lcWindowStartTiming + (progress * (this.ecu.lcTimingAbsolute - this.ecu.lcWindowStartTiming));
                
                // Fuel adder transition (%)
                launchFuelAdder = progress * this.ecu.lcFuelAdder;
            }
        } else {
            this.lcActive = false;
        }

        // Fetch proper VVT / Base state based on ECU commanded targets
        const mapKpaGuess = 12 + (tps * 0.9); // Initial guess for table lookups
        const ecuIntakeAngle = this.ecu.getTargetVvtIntake(rpm, mapKpaGuess) || 0;
        const ecuExhaustAngle = this.ecu.getTargetVvtExhaust(rpm, mapKpaGuess) || 0;
        const ecuCamSwitchRpm = this.ecu.camSwitchRpm || 5500;
        const ecuCamSwitchTps = this.ecu.camSwitchTps || 20;

        const mode = this.engine.vvt_parameters?.vvt_mode || "None";

        // VTEC (VVL) Switching Logic: If vvl_delete is true, we force it to TRUE (VTEC Killer)
        // In non-advanced mode, VVT subsystem is fully disabled — always uses base/low cam profile
        let vvlActive = advancedMode && (rpm >= ecuCamSwitchRpm && tps >= ecuCamSwitchTps && !modModifiers.vvt_delete && !!this.mapCache["High Cam Profile"]);
        if (advancedMode && modModifiers.vvl_delete && !!this.mapCache["High Cam Profile"]) {
            vvlActive = true;
        }
        let actualIntakeAngle = 0;
        let actualExhaustAngle = 0;
        let vvtTargetIn  = 0;
        let vvtTargetEx  = 0;

        if (advancedMode && !modModifiers.vvt_delete) {
            if (mode === "Intake" || mode === "Dual") {
                const maxIn = this.getMaxVvtAngle("In");
                vvtTargetIn = (maxIn > 0) ? Math.min(ecuIntakeAngle, maxIn) : ecuIntakeAngle;
                if (advancedMode && this.ecu.vvtPhaserEnabled) {
                    const r = this._vvtPidStep(vvtTargetIn, this.actualVvtIntake, this.vvtIntIn, this.vvtErrIn,
                        this.ecu.vvtKp, this.ecu.vvtKi, this.ecu.vvtKd, dt);
                    this.actualVvtIntake = Math.max(0, this.actualVvtIntake + r.output * dt);
                    if (maxIn > 0) this.actualVvtIntake = Math.min(maxIn, this.actualVvtIntake);
                    this.vvtIntIn = r.integral;
                    this.vvtErrIn = r.prevErr;
                } else {
                    this.actualVvtIntake = vvtTargetIn;
                    this.vvtIntIn = 0; this.vvtErrIn = 0;
                }
                actualIntakeAngle = this.actualVvtIntake;
            } else {
                this.actualVvtIntake = 0; this.vvtIntIn = 0; this.vvtErrIn = 0;
            }
            if (mode === "Dual") {
                const maxEx = this.getMaxVvtAngle("Ex");
                vvtTargetEx = (maxEx > 0) ? Math.max(ecuExhaustAngle, -maxEx) : ecuExhaustAngle;
                if (advancedMode && this.ecu.vvtPhaserEnabled) {
                    const r = this._vvtPidStep(vvtTargetEx, this.actualVvtExhaust, this.vvtIntEx, this.vvtErrEx,
                        this.ecu.vvtKp, this.ecu.vvtKi, this.ecu.vvtKd, dt);
                    this.actualVvtExhaust = Math.min(0, this.actualVvtExhaust + r.output * dt);
                    if (maxEx > 0) this.actualVvtExhaust = Math.max(-maxEx, this.actualVvtExhaust);
                    this.vvtIntEx = r.integral;
                    this.vvtErrEx = r.prevErr;
                } else {
                    this.actualVvtExhaust = vvtTargetEx;
                    this.vvtIntEx = 0; this.vvtErrEx = 0;
                }
                actualExhaustAngle = this.actualVvtExhaust;
            } else {
                this.actualVvtExhaust = 0; this.vvtIntEx = 0; this.vvtErrEx = 0;
            }
        } else {
            this.actualVvtIntake  = 0; this.vvtIntIn = 0; this.vvtErrIn = 0;
            this.actualVvtExhaust = 0; this.vvtIntEx = 0; this.vvtErrEx = 0;
        }

        const minMapRpm = this.mapCache["Base"]?.rpms[0] ?? this.mapCache["Low Cam Profile"]?.rpms[0] ?? 500;
        const lookupRpm = modModifiers.powerband_shift > 0 ? Math.max(rpm - modModifiers.powerband_shift, minMapRpm) : rpm;
        let base = this.calculateEngineVVTState(lookupRpm, actualIntakeAngle, actualExhaustAngle, vvlActive, modModifiers.vvt_delete);

        // Apply N.A. Upgrades to Engine Characteristics
        base.torque *= modModifiers.torque_mult;
        base.ve *= modModifiers.ve_mult;
        base.timing += modModifiers.timing_adder;

        // Flex Fuel Baseline Physics Changes (Advanced Mode only)
        const eth = advancedMode ? (this.ecu.ethanolContent || 0) : 0;
        let effectiveOctane = this.ecu.fuelOctane || 91;
        if (eth > 0) {
            const ethRatio = eth / 100.0;
            base.torque *= 1.0 + (0.05 * ethRatio); // Up to 5% ideal torque bonus
            base.timing += (3.0 * ethRatio);        // Up to 3 degrees ideal MBT shift
            base.afr -= (0.2 * ethRatio);           // Up to 0.2 richer ideal AFR
            effectiveOctane += (ethRatio * 20.0);   // E100 effectively adds ~20 points of knock resistance
        }

        // Octane Mismatch MBT Shift
        const requiredOctane = this.engine.global_specs.required_octane || 91;
        if (effectiveOctane < requiredOctane) {
            const octaneDeficit = requiredOctane - effectiveOctane;
            // Exponential drop in ideal MBT timing: 1pt deficit = ~1.5 deg, 4pt deficit = ~15 deg
            const mbtShift = Math.pow(octaneDeficit, 1.9) * 1.0;
            base.timing -= mbtShift;
        }

        // --- NITROUS OXIDE IDEAL MAP MODIFIERS ---
        // N2O decomposes to supply extra O2, shifting the engine's ideal operating points.
        // These run before commandedVe/targetAfr/actualTiming are read, so they feed into all
        // downstream calculations (injector PW, AFR error, torque efficiency, knock model).
        if (nitrousActive && this.ecu.nitrousEnabled) {
            const n2oHp = this.ecu.nitrousCalculatedHp;
            base.timing -= (n2oHp / 100) * 1.5; // Faster N2O combustion needs less advance (~1.5° per 100 HP)
            base.ve    += n2oHp * 0.05;          // Extra O2 raises effective VE (~+5% per 100 HP)
            base.afr   -= n2oHp * 0.003;         // More O2 → ideal power AFR shifts richer (~−0.3 per 100 HP)
        }

        let throttleScale = this.engine.mechanical_limits.idle_throttle_area_scale || 1.0;
        let effectiveTps = tps * throttleScale;
        
        // Cams adjust the idle vacuum. Big overlap means less vacuum (higher kPa)
        let restingVacuum = 12 + modModifiers.vacuum_penalty;
        let nAMapKpa = restingVacuum + (effectiveTps * 5.5);
        if (nAMapKpa > baroKpa) nAMapKpa = baroKpa;

        // MAP LAG — exponential filter simulating manifold filling time
        // Active only when accel enrichment mode is enabled; τ fades to 0 above 2000 RPM
        if (advancedMode && this.ecu.accelMode !== 'none') {
            if (this.filteredMapKpa < 0) this.filteredMapKpa = nAMapKpa;
            const rpmLow = 800, rpmHigh = 2000, tauMax = 0.3;
            const tau = rpm <= rpmLow ? tauMax
                      : rpm >= rpmHigh ? 0
                      : tauMax * (1 - (rpm - rpmLow) / (rpmHigh - rpmLow));
            if (tau < 0.001) {
                this.filteredMapKpa = nAMapKpa;
            } else {
                this.filteredMapKpa += (dt / (tau + dt)) * (nAMapKpa - this.filteredMapKpa);
            }
            nAMapKpa = Math.min(this.filteredMapKpa, baroKpa);
        } else {
            this.filteredMapKpa = nAMapKpa;  // keep in sync for instant mode switch
        }

        // ACCEL ENRICHMENT STATE — trigger and decay
        if (advancedMode && this.ecu.accelMode !== 'none' && !isCranking) {
            if (dTps > 0 && dTps >= (this.ecu.accelMsDtpsAxis?.[0] ?? 0)) {
                if (this.ecu.accelMode === 'ms_adder') {
                    const newMs = this.ecu.getAccelMsAdder(rpm, dTps);
                    if (newMs > this.accelCurrentMs) {  // top up only, never reset downward
                        this.accelCurrentMs = newMs;
                        this.accelTimer = 0;
                    }
                } else if (this.ecu.accelMode === 'map_predict') {
                    this.accelBlend = 1.0;
                    this.accelTimer = 0;
                }
            }
            this.accelTimer += dt;
            const timedOut = this.accelTimer >= this.ecu.accelTime;
            if (this.ecu.accelMode === 'ms_adder') {
                this.accelCurrentMs = timedOut ? 0 : Math.max(0, this.accelCurrentMs - this.ecu.accelDecayRate * dt);
            } else if (this.ecu.accelMode === 'map_predict') {
                this.accelBlend = timedOut ? 0 : Math.max(0, this.accelBlend - this.ecu.accelDecayRate * dt);
            }
        }

        let targetAfr = this.ecu.getTargetAfr(rpm, mapKpaGuess);
        let actualTiming = this.ecu.getTiming(rpm, mapKpaGuess) + idleTimingAdj;

        // --- APPLY LAUNCH CONTROL ADDERS ---
        if (isLcArmed) {
            if (launchTimingOverride !== null) {
                actualTiming = launchTimingOverride;
            }
            // Fuel adder is % enrichment. targetAfr = targetAfr / (1 + adder/100)
            targetAfr /= (1 + (launchFuelAdder / 100.0));
        }

        // Initialize IAT with ambient plus existing heat soak components
        let dynamicIat = ambientTempF + (this.turboHeatAccumulatedC * (9 / 5)) + (this.scHeatAccumulatedC * 0.5);
        let activeBoostPsi = 0;
        let mapKpa = nAMapKpa;
        let currentCfm = 0;
        let turboSpeed = 0;
        let turboEff = 100;
        let waMassflow = 0;
        let compressorPR = 1.0;

        let dispCi = this.engine.global_specs.displacement_cc / 16.387;
        const disp_L = this.engine.global_specs.displacement_cc / 1000.0;
        
        let turboHpGain = 0;
        let scHpGain = 0;
        let scDragHp = 0;

        if (modModifiers.turboProfile) {
            let tp = modModifiers.turboProfile;
            let power_per_psi = tp.power_per_psi || 15;
            let iat_per_psi = tp.iat_per_psi || 1.5;
            let max_turbo_hp = tp.max_turbo_hp || (tp.max_airflow_cfm ? tp.max_airflow_cfm / 1.45 : 500);
            let spool_turbo_begin_hp = tp.spool_turbo_begin_hp || tp.spool_threshold_hp || 50;
            let turbo_spool_onset_hp = tp.turbo_spool_onset_hp || tp.turbine_inertia || 2;

            // --- NEW TURBO HEURISTIC MODEL ---
            // max_turbo_hp acts as our "choke anchor"
            let na_hp = (base.torque * rpm) / 5252;
            let anchor = max_turbo_hp;
            let limit_start = 0.85 * anchor; // 15% before anchor
            let iat_start   = 0.90 * anchor; // 10% before anchor
            let hard_limit  = 1.10 * anchor; // 10% over anchor (brick wall)

            // WG duty base from table
            let wgDutyBase = this.ecu.getWgDuty(rpm, tps);
            if (advancedMode && eth > 0) wgDutyBase += this.ecu.getFlexBoostAdder(rpm, tps, eth);

            // Apply Gear-Based Boost Duty Compensation
            if (advancedMode) {
                let gear = 1;
                if (typeof getCurrentGear === 'function') {
                    gear = getCurrentGear();
                }
                wgDutyBase += this.ecu.getGearBoostDutyComp(gear);
            }

            // Closed-loop boost PID layer (advanced mode only)
            let wgDutyCmd;
            if (advancedMode && this.ecu.clBoostEnabled) {
                let targetPsi = this.ecu.getClBoostTarget(rpm, tps);
                if (targetPsi <= 0) targetPsi = this.ecu.wgSpringKpa * 0.14504;

                // Apply Gear-Based Boost Target Compensation
                let gear = 1;
                if (typeof getCurrentGear === 'function') {
                    gear = getCurrentGear();
                }
                targetPsi += this.ecu.getGearBoostTargetComp(gear);
                if (targetPsi < 0) targetPsi = 0; // prevent negative target

                const actualBoostPsi = this.actualBoostKpa / 6.89476;
                const err = targetPsi - actualBoostPsi;

                const newIntegral = Math.max(-50, Math.min(50, this.clBoostIntegral + err * dt));
                const deriv = dt > 0 ? (err - this.clBoostPrevErr) / dt : 0;
                const pidAdj = this.ecu.clBoostKp * err
                             + this.ecu.clBoostKi * newIntegral
                             + this.ecu.clBoostKd * deriv;
                this.clBoostIntegral = newIntegral;
                this.clBoostPrevErr  = err;

                wgDutyCmd = Math.max(0, Math.min(100, wgDutyBase + pidAdj));
            } else {
                this.clBoostIntegral = 0;
                this.clBoostPrevErr  = 0;
                wgDutyCmd = wgDutyBase;
            }

            // 300ms first-order lag on effective WG duty (τ = 0.3s)
            const wgLagAlpha = dt / (0.3 + dt);
            this.effectiveWgDuty += wgLagAlpha * (wgDutyCmd - this.effectiveWgDuty);

            let wgDuty = this.effectiveWgDuty;
            let targetBoostPsi = this.ecu.wgSpringKpa * 0.14504 * (1.0 + (wgDuty / 90.0));
            
            // Calculate potential total HP if we hit target boost perfectly
            let potentialTotalHp = na_hp + (targetBoostPsi * power_per_psi);

            // Boost Drop Logic
            let effectiveTargetBoostKpa = targetBoostPsi * 6.89476;
            if (potentialTotalHp > limit_start) {
                // Determine how much we are "choking" (0.0 at limit_start to 1.0 at hard_limit)
                let range = hard_limit - limit_start;
                let chokeProgress = Math.min(1.0, (potentialTotalHp - limit_start) / range);
                
                // Boost duty cycle can slightly mitigate the drop (up to 15% relief at 100% duty)
                let dutyRelief = (wgDuty / 100.0) * 0.15;
                let finalChoke = Math.max(0, chokeProgress - dutyRelief);
                
                // Absolute physical ceiling to keep power at hard_limit (the "brick wall")
                let maxAllowedTurboHp = Math.max(0, hard_limit - na_hp);
                let maxAllowedBoostKpa = (maxAllowedTurboHp / Math.max(0.1, power_per_psi)) * 6.89476;
                
                // The "soft drop" starts at limit_start. We fade from 100% of target 
                // down to a lower percentage (e.g. 65%) to simulate efficiency loss.
                let softDropKpa = effectiveTargetBoostKpa * (1.0 - finalChoke * 0.35);
                
                // Final boost target is the MINIMUM of our soft drop and the hard power ceiling.
                // This ensures we always drop below the original target and never exceed hard_limit.
                effectiveTargetBoostKpa = Math.min(softDropKpa, maxAllowedBoostKpa);
            }

            let maxPr = tp.max_pressure_ratio || 4.0;
            let absMaxBoostKpa = (maxPr - 1) * baroKpa;
            let targetBoostKpa = Math.min(effectiveTargetBoostKpa, absMaxBoostKpa);

            // Scale target boost by TPS: 0% at ≤20% TPS, 100% at ≥80% TPS
            // This creates a genuine partial-throttle boost ceiling, not just slower spool
            let tpsBoostScale = Math.max(0, Math.min(1.0, (tps - 20) / 60));
            targetBoostKpa *= tpsBoostScale;

            // Pre-calculate physical VE purely for airflow spool calculation
            let spoolVeVacuumDrop = 0.4 + 0.6 * (nAMapKpa / baroKpa);
            let spoolPhysicalVe = base.ve * spoolVeVacuumDrop;
            const _prevBoostPsi = this.actualBoostKpa / 6.89476;
            const _boostVeScale = 1.0 + _prevBoostPsi * 0.005;
            currentCfm = (dispCi * rpm * 0.5 * (spoolPhysicalVe * _boostVeScale / 100.0)) / 1728.0;

            // --- LAUNCH CONTROL TURBO LOGIC ---
            if (this.ecu.lcEnabled && (isDragStaging || this.ecu.twoStepSwitchActive)) {
                let launchAirflowMult = 0.4;
                const mbtRetard = base.timing - actualTiming;
                if (mbtRetard > 0) {
                    launchAirflowMult = 0.4 + (mbtRetard / 25.0) * 0.6;
                }
                currentCfm *= launchAirflowMult;
            }

            let drivingHP = ((this.lastTorque > 0 ? this.lastTorque : base.torque) * rpm) / 5252;
            
            // Allow launch control to generate driving HP via anti-lag (retarded timing heat)
            if (isLcArmed && (base.timing - actualTiming) > 10) {
                drivingHP += ((base.timing - actualTiming) * 5); // Fake heat energy for spooling on 2-step
            }

            // Scale spool rate by TPS: 0% at ≤20% TPS, 100% at ≥80% TPS
            let tpsSpoolScale = Math.max(0, Math.min(1.0, (tps - 20) / 60));
            if (drivingHP > spool_turbo_begin_hp && tpsSpoolScale > 0) {
                let excessHP = drivingHP - spool_turbo_begin_hp;
                let boostRateKpaPerS = (excessHP / turbo_spool_onset_hp) * 6.89476 * tpsSpoolScale;
                let targetDeltaKpa = targetBoostKpa - this.actualBoostKpa;
                
                if (targetDeltaKpa > 0) {
                    this.actualBoostKpa += Math.min(boostRateKpaPerS * dt, targetDeltaKpa);
                } else if (targetDeltaKpa < 0) {
                    // Fast bleed-off when target is lower (choking or WG opening)
                    let bleedRateKpaPerS = 250.0; // Rapid drop to simulate physical choke / pressure loss
                    this.actualBoostKpa += Math.max(-bleedRateKpaPerS * dt, targetDeltaKpa);
                }
            } else {
                this.actualBoostKpa *= Math.max(0, 1 - (8.0 * dt));
            }

            this.actualBoostKpa = Math.max(0, this.actualBoostKpa);
            activeBoostPsi = this.actualBoostKpa / 6.89476;
            
            // Dummy turbo speed for telemetry based on boost level
            let maxTurboRpm = tp.max_turbo_rpm || 150000;
            let estimatedMaxBoost = (tp.max_pressure_ratio || 4.0) * baroKpa;
            turboSpeed = Math.min(maxTurboRpm * 1.1, (this.actualBoostKpa / estimatedMaxBoost) * maxTurboRpm);
            this.turboSpeedRpm = turboSpeed;

            // Final HP Gain and IAT calculation
            let currentTotalHp = na_hp + (activeBoostPsi * power_per_psi);
            let totalHeatPerSec = activeBoostPsi * iat_per_psi;
            turboEff = 100; // Reset to 100 for base heuristic

            if (currentTotalHp > iat_start) {
                let iatRange = hard_limit - iat_start;
                let iatProgress = Math.min(1.0, (currentTotalHp - iat_start) / iatRange);
                
                // Exponential heat rise as we approach and exceed limits
                let heatMult = 1.0 + Math.pow(iatProgress, 1.5) * 4.0; 
                totalHeatPerSec *= heatMult;
                
                // Scale displayed efficiency down based on heat stress
                turboEff = Math.max(10, 100 / heatMult);

                if (currentTotalHp >= hard_limit) {
                    turboHpGain = hard_limit - na_hp; // Power brick wall
                } else {
                    turboHpGain = activeBoostPsi * power_per_psi;
                }
            } else {
                turboHpGain = activeBoostPsi * power_per_psi;
            }
            
            if (totalHeatPerSec > 0) {
                this.turboHeatAccumulatedC += totalHeatPerSec * dt;
            }
        }

        // Supercharger Boost Physics
        this.scBoostKpa = 0;
        let scBoostPsi = 0;
        let activeScExponent = 0;
        let activeScChoke = 0;
        if (modModifiers.scProfile) {
            const sc = modModifiers.scProfile;
            const pulleyRatio = (this.ecu.scPulleyRatio || 2.5);
            const scRpm = rpm * pulleyRatio;
            scBoostPsi = 0;
            let scEffDrop = 1.0;

            // 1. Mechanical RPM Limit & Efficiency Drop
            const maxScRpm = sc.max_sc_rpm || 18000;
            if (scRpm > maxScRpm) {
                // Efficiency falls off rapidly beyond max_sc_rpm
                const overspeedRatio = (scRpm - maxScRpm) / maxScRpm;
                scEffDrop = Math.max(0.1, 1.0 - (overspeedRatio * 2.5));
                
                // Catastrophic failure risk if overspun significantly (15%+)
                if (overspeedRatio > 0.15) {
                    const failChancePerS = (overspeedRatio - 0.15) * 0.5;
                    if (Math.random() < failChancePerS * dt) {
                        this._blowEngine("Supercharger exploded from over-spinning.");
                        return { error: "Catastrophic Failure: Your supercharger exploded from over-spinning!", valid: false };
                    }
                }
            }

            if ((sc.sc_type === 'roots' || sc.sc_type === 'twin-screw') && sc.sc_displacement_l) {
                // --- REFINED PD SUPERCHARGER PSI-CENTRIC MODEL ---
                const bypassThreshold = this.ecu.scBypassThreshold ?? sc.bypass_tps_threshold ?? 15;
                
                if (tps > bypassThreshold) {
                    // 1. Calculate Volumetric Efficiency (VE) using quadratic coefficients
                    const [veA, veB, veC] = sc.sc_ve_coeffs || [0, 0, 0.90];
                    let scVE = (veA * Math.pow(scRpm, 2)) + (veB * scRpm) + veC;
                    scVE = Math.max(0.1, Math.min(1.2, scVE)) * scEffDrop;

                    // 2. Adiabatic Efficiency (AE)
                    const [aeA, aeB, aeC] = sc.sc_ae_coeffs || [0, 0, 0.65];
                    let scAE = (aeA * Math.pow(scRpm, 2)) + (aeB * scRpm) + aeC;
                    scAE = Math.max(0.1, Math.min(0.95, scAE));

                    // 3. Theoretical Boost (PSI) = ((Lsc * Ratio) / (Leng / 2) - 1) * 14.7
                    const theoreticalBoostPsi = ((sc.sc_displacement_l * pulleyRatio) / (disp_L / 2) - 1) * 14.7;
                    
                    // User's PSI-centric request: multiply theoretical by VE
                    scBoostPsi = Math.max(0, theoreticalBoostPsi * scVE);

                    // 4. PSI-Centric Power Gain
                    const scPowerPerPsi = sc.power_per_psi || 15;
                    scHpGain = scBoostPsi * scPowerPerPsi;

                    // 5. Parasitic Drag Calculation
                    const fDrag = sc.fixed_drag_hp || 4.0;
                    const dCoeff = sc.drag_coeff || 0.00004;
                    scDragHp = fDrag + (dCoeff * Math.pow(scRpm, 1.5));

                    // 6. Heat Soak Accumulation
                    // Heat increases with boost and RPM; scales with (1 - AE)
                    const heatGen = (scBoostPsi * 2.0) * (1.1 - scAE) * (scRpm / maxScRpm);
                    this.scHeatAccumulatedC += heatGen * dt;

                    // Thermodynamic IAT rise from compression
                    const scEff = sc.efficiency_percent ?? (sc.sc_ae_coeffs ? 70 : 65);
                    const _scPr = (baroKpa + (scBoostPsi * 6.89476)) / baroKpa;
                    const _scAmbK = (ambientTempF - 32) * 5 / 9 + 273.15;
                    const thermoIatRiseF = _scAmbK * (Math.pow(_scPr, 0.283) - 1) / (scEff / 100) * 9 / 5;
                    dynamicIat += thermoIatRiseF;
                } else {
                    // Bypass open: zero boost, minimal drag
                    scBoostPsi = 0;
                    scDragHp = (sc.fixed_drag_hp || 4.0) * 0.1;
                }
            } else if (sc.sc_type === 'roots' || sc.sc_type === 'twin-screw') {
                // Legacy PD SC Model
                if (tps > (sc.bypass_tps_threshold ?? 15)) {
                    const rpmFraction = Math.min(scRpm / maxScRpm, 1.0);
                    const lowRpmVe    = Math.min(rpmFraction / 0.25, 1.0);
                    const highRpmDrop = Math.max(0, (rpmFraction - 0.75) / 0.25) * 0.08;
                    const blowerVe    = lowRpmVe * (1.0 - highRpmDrop) * scEffDrop;
                    const pulleyScale = pulleyRatio / (sc.reference_pulley_ratio || 2.5);
                    scBoostPsi = blowerVe * sc.max_boost_psi * pulleyScale;
                    
                    const scPowerPerPsi = sc.power_per_psi || 15;
                    scHpGain = scBoostPsi * scPowerPerPsi;
                    scDragHp = (sc.fixed_drag_hp || 5) + (sc.drag_coeff || 0.00005) * Math.pow(scRpm, 1.5);

                    const scEff = sc.efficiency_percent ?? 68;
                    const _scPr = (baroKpa + (scBoostPsi * 6.89476)) / baroKpa;
                    const _scAmbK = (ambientTempF - 32) * 5 / 9 + 273.15;
                    dynamicIat += _scAmbK * (Math.pow(_scPr, 0.283) - 1) / (scEff / 100) * 9 / 5;
                }
            } else if (sc.sc_type === 'centrifugal') {
                // --- SIMPLIFIED CENTRIFUGAL MODEL ---
                // Base boost follows a fixed RPM power law. 
                // Pulley ratio then pushes the entire curve up or down as a flat offset.
                
                const na_hp = (base.torque * rpm) / 5252;
                const maxScHp = sc.max_sc_hp || 600;
                const pulleyRatio = (this.ecu.scPulleyRatio || 2.5);
                const refPulley = sc.reference_pulley_ratio || 2.5;
                const scRpm = rpm * pulleyRatio;
                const maxScRpm = sc.max_sc_rpm || 18000;
                
                // 1. Calculate Exponent (sizeFactor) based on Engine Size Anchor + Pulley Ratio
                // centered at 1.0. Bigger engine (maxNaHp > hpMid) = smaller exponent = less boost.
                let sizeFactor = 1.0;
                if (sc.base_hp_min && sc.base_hp_max) {
                    const hpMin = sc.base_hp_min;
                    const hpMax = sc.base_hp_max;
                    const hpMid = (hpMin + hpMax) / 2;
                    const hpRange = Math.max(1, (hpMax - hpMin) / 2);
                    
                    sizeFactor = 1.0 - ((this.maxNaHp - hpMid) / hpRange) * 0.1;
                }

                // Pulley Ratio influence on exponent: +1.0 ratio = +0.1 to exponent
                sizeFactor += (pulleyRatio - refPulley) * 0.15;
                sizeFactor = Math.max(0.6, Math.min(2.0, sizeFactor)); 
                activeScExponent = sizeFactor;

                // Base boost curve using the requested formula
                const baseBoost = Math.pow(rpm / 1000, sizeFactor);
                
                // 2. Apply Pulley Ratio Offset
                // Rule: +1.0 ratio = +1.0 PSI flat shift
                const boostOffset = (pulleyRatio - refPulley) * 1.0;
                
                scBoostPsi = Math.max(0, baseBoost + boostOffset) * scEffDrop;

                // 3. Barometric Correction
                // Boost drops as ambient pressure falls below sea level (101.3 kPa)
                const baroLossPsi = (101.3 - baroKpa) / 6.89476;
                scBoostPsi = Math.max(0, scBoostPsi - baroLossPsi);

                // --- BOV Logic for Centrifugal SC ---
                let bovFactor = 1.0;
                if (nAMapKpa < 90) {
                    bovFactor = 0;
                } else if (nAMapKpa < 98) {
                    bovFactor = (nAMapKpa - 90) / 8; // Linear ramp from 90 to 98 kPa
                }
                scBoostPsi *= bovFactor;

                // --- CHOKE ANCHOR (Power-Centric Physics) ---
                // If total power exceeds blower capacity, boost drops and heat spikes.
                // We allow 20% headroom over maxScHp with increasing choke.
                const scPowerPerPsi = sc.power_per_psi || 15;
                const potentialScHp = scBoostPsi * scPowerPerPsi;
                const totalTheoreticalHp = na_hp + potentialScHp;
                
                let chokeProgress = 0;
                if (totalTheoreticalHp > maxScHp * 0.85) {
                    const limitStart = maxScHp * 0.85;
                    const headRoom   = maxScHp * 1.20; // 20% over-rating allowance
                    const range = headRoom - limitStart;
                    chokeProgress = Math.min(1.0, (totalTheoreticalHp - limitStart) / range);
                    
                    // Apply boost drop (up to 40% reduction at headroom limit)
                    scBoostPsi *= (1.0 - (chokeProgress * 0.40));
                    
                    // Hard choke if we still exceed the headroom limit
                    const finalTotalHp = na_hp + (scBoostPsi * scPowerPerPsi);
                    if (finalTotalHp > headRoom) {
                        const excess = finalTotalHp - headRoom;
                        scBoostPsi = Math.max(0, (headRoom - na_hp) / Math.max(1, scPowerPerPsi));
                    }
                    
                    // Heat spike
                    const heatGen = (chokeProgress * 15.0) * (scRpm / maxScRpm);
                    this.scHeatAccumulatedC += heatGen * dt;
                }
                activeScChoke = chokeProgress;

                scHpGain = scBoostPsi * scPowerPerPsi;
                scDragHp = (sc.fixed_drag_hp || 2) + (sc.drag_coeff || 0.00002) * Math.pow(scRpm, 1.5);
                
                // Reduce drag when BOV is open (vacuum condition) to simulate idle bypass
                scDragHp *= (0.1 + 0.9 * bovFactor);

                const scEff = sc.efficiency_percent ?? 72;
                const _scPr = (baroKpa + (scBoostPsi * 6.89476)) / baroKpa;
                const _scAmbK = (ambientTempF - 32) * 5 / 9 + 273.15;
                dynamicIat += _scAmbK * (Math.pow(_scPr, 0.283) - 1) / (scEff / 100) * 9 / 5;
            }

            this.scBoostKpa = Math.max(0, scBoostPsi * 6.89476);
            
            // Apply net SC power (Gain - Drag)
            scHpGain -= scDragHp;
        }

        // Decay logic for accumulated heat
        this.turboHeatAccumulatedC *= Math.max(0, 1 - (0.5 * dt));
        this.scHeatAccumulatedC *= Math.max(0, 1 - (0.2 * dt));

        // Merge turbo + SC boost; apply twin-charge efficiency penalty if both active
        {
            let turboBoostKpa = this.actualBoostKpa;
            let scBoostKpa    = this.scBoostKpa;
            let combinedBoostKpa = turboBoostKpa + scBoostKpa;

            if (modModifiers.turboProfile && modModifiers.scProfile) {
                combinedBoostKpa *= (modModifiers.scProfile.twin_charge_efficiency ?? 0.82);
            }

            mapKpa = Math.min(nAMapKpa, baroKpa) + (combinedBoostKpa * (tps / 100.0));
            activeBoostPsi = combinedBoostKpa / 6.89476;
        }

        // Save pre-prediction MAP for telemetry (what the sensor actually reads after lag)
        const actualMapKpa = mapKpa;

        // MAP PREDICTION BLEND — replace mapKpa with tuned predicted MAP during tip-in
        if (advancedMode && this.ecu.accelMode === 'map_predict' && this.accelBlend > 0) {
            const predicted = this.ecu.getAccelPredictedMap(rpm, tps);
            mapKpa = mapKpa * (1 - this.accelBlend) + predicted * this.accelBlend;
            mapKpa = Math.max(20, mapKpa);
        }
        // mapKpa is now the effective MAP used by all table lookups downstream

        // Apply Intercooler / Cooling Mod Reduction (covers heat from both turbo and SC)
        if (modModifiers.iat_reduction_percent > 0) {
            const heatAboveAmbient = Math.max(0, dynamicIat - ambientTempF);
            dynamicIat -= (heatAboveAmbient * modModifiers.iat_reduction_percent);
        }

        // Check Boost Cut (with hysteresis) — applies to any forced induction
        if (modModifiers.turboProfile || modModifiers.scProfile) {
            const boostCutThreshKpa  = baroKpa + this.ecu.boostCutKpa;
            const boostCutResumeKpa  = boostCutThreshKpa - this.ecu.boostCutHysteresisKpa;
            if (mapKpa >= boostCutThreshKpa) {
                this.boostCutActive = true;
            } else if (this.boostCutActive && mapKpa <= boostCutResumeKpa) {
                this.boostCutActive = false;
            }
        } else {
            this.boostCutActive = false;
        }

        let tableVe = this.ecu.getVE(rpm, mapKpa);
        let commandedVe = tableVe;
        if (isCranking) {
            commandedVe = this.ecu.crankingVe;
        } else if (crankDecay > 0) {
            commandedVe = (this.ecu.crankingVe * crankDecay) + (tableVe * (1 - crankDecay));
        }
        // Post-crank fuel enrichment multiplier (decays to 1.0× over N engine cycles)
        if (postCrankEnrich > 1.001) commandedVe *= postCrankEnrich;

        // Fetch actual values for mapKpa (re-using variables from earlier)
        targetAfr = this.ecu.getTargetAfr(rpm, mapKpa);

        // Forced cranking AFR for Normal Mode
        if (isCranking && !advancedMode) {
            targetAfr = 14.3;
        }

        actualTiming = this.ecu.getTiming(rpm, mapKpa) + idleTimingAdj;

        // Flex Fuel Table Lookups (Advanced Mode only — eth is already 0 in simple mode)
        if (eth > 0) {
            commandedVe += this.ecu.getFlexVeAdder(rpm, mapKpa, eth);
            targetAfr += this.ecu.getFlexAfrAdder(rpm, mapKpa, eth);
            actualTiming += this.ecu.getFlexTimingAdder(rpm, mapKpa, eth);
        }

        // Apply global VVL ECU tuning adders directly to commanded fuel and spark
        if (vvlActive) {
            commandedVe += this.ecu.vvlFuelAdder;
            actualTiming += this.ecu.vvlTimingAdder;
        }

        // --- NITROUS ECU CORRECTIONS & TORQUE ADDER ---
        let nitrousTorqueAdder = 0;
        if (nitrousActive && this.ecu.nitrousEnabled && this.ecu.nitrousCalculatedHp > 0) {
            nitrousTorqueAdder = (this.ecu.nitrousCalculatedHp * 5252) / Math.max(rpm, 500);
            // Wet shot: fuel displacing intake air costs ~2% of the nitrous HP contribution
            if (this.ecu.nitrousShotType === 'wet') nitrousTorqueAdder *= 0.98;
            if (this.ecu.nitrousActivationMode === 'ecu') {
                // ECU mode: user-dialed VE compensation and timing retard (works for both shot types)
                commandedVe *= (1 + this.ecu.nitrousVeCompPct / 100);
                actualTiming -= this.ecu.nitrousTimingRetard;
            }
            // Dry switch: user bakes fuel/timing comp into maps — no sim correction applied
            // Wet switch: kit's fuel solenoid handles base enrichment — no additional ECU correction
        }

        // --- APPLY LAUNCH CONTROL ADDERS AGAIN (for final mapKpa) ---
        // Absolute timing override MUST happen after all other timing adders (idle, flex, vvl)
        if (isLcArmed) {
            if (launchTimingOverride !== null) {
                actualTiming = launchTimingOverride;
            }
            targetAfr /= (1 + (launchFuelAdder / 100.0));
        }

        // IAT Compensation
        const iatCelsius = (dynamicIat - 32) * 5 / 9;
        // VE curve: user-tuned 6-point curve in °C — the only thing that modifies commandedVe from IAT
        const iatVeCorr = Interpolation.interpolate1D(iatCelsius, this.ecu.iatVeAxis, this.ecu.iatVeCurve);
        commandedVe *= iatVeCorr;
        // ECT fuel enrichment: cold-start correction (multiplier applied to commanded VE)
        const coolantC = (typeof currentCoolantC !== 'undefined' && currentCoolantC !== null) ? currentCoolantC : 90;
        const ectFuelMult = Interpolation.interpolate1D(coolantC, this.ecu.ectFuelAxis, this.ecu.ectFuelCurve);
        if (ectFuelMult !== 1) commandedVe *= ectFuelMult;
        // IAT timing retard — directly from user curve (degrees retard at each IAT in °C)
        const iatTimingRetard = Interpolation.interpolate1D(iatCelsius, this.ecu.iatTimingAxis, this.ecu.iatTimingCurve);
        actualTiming -= iatTimingRetard;
        // Knock control retard — included in ecuCommandedTiming so telemetry shows actual firing angle
        if (knockRetardDeg > 0) actualTiming -= knockRetardDeg;

        const octane = effectiveOctane; // Use consistency with flex fuel calculations

        // Determine boost penalty reduction based on fuel octane
        let octaneBonus = 0;
        if (octane >= 116) {
            octaneBonus = 1.0; // 100% reduction in penalty
        } else if (octane >= 100) {
            // Linear scale from 20% bonus at 100 to 100% bonus at 116
            octaneBonus = 0.2 + (octane - 100) * (0.8 / 16);
        } else if (octane > 91) {
            // Linear scale from 0% bonus at 91 to 20% bonus at 100
            octaneBonus = (octane - 91) * (0.2 / 9);
        }
        const octaneMultiplier = Math.max(0, 1.0 - octaneBonus);

        // Individual PSI for per-device modifier calculations
        const turboPsi = this.actualBoostKpa / 6.89476;
        const scPsi    = this.scBoostKpa / 6.89476;

        // Apply Turbo Heat, Timing, and AFR shifts based on turbo's own PSI
        // Shifts the engine's ideal MBT and ideal AFR downward
        if (modModifiers.turboProfile && turboPsi > 0) {
            // 1. Timing Retard - Preserving the "characteristic" knee at 25psi
            let retardPerPsi = modModifiers.turboProfile.timing_retard_per_psi || 0;

            let totalRetard = 0;
            if (turboPsi > 25) {
                totalRetard = (25 * retardPerPsi) + ((turboPsi - 25) * (retardPerPsi * 0.5));
            } else {
                totalRetard = turboPsi * retardPerPsi;
            }
            base.timing -= totalRetard;

            // 2. VE Gain per PSI (Ideal VE increases with boost pressure)
            let veGainPerPsi = modModifiers.turboProfile.ve_per_psi;
            if (veGainPerPsi === undefined) veGainPerPsi = 0.005; // default to legacy 0.005 if not defined
            base.ve *= (1 + turboPsi * veGainPerPsi);

            // 3. AFR Enrichment (Ideal AFR becomes richer) - Purely Linear
            let afrAdderPerPsi = (modModifiers.turboProfile.afr_adder_per_psi || 0) * octaneMultiplier;

            base.afr -= (turboPsi * afrAdderPerPsi);
        }

        // Apply Supercharger Heat, Timing, and AFR shifts based on SC's own PSI
        if (modModifiers.scProfile && scPsi > 0) {
            const sc = modModifiers.scProfile;

            // 1. Timing Retard
            let scRetardPerPsi = sc.timing_retard_per_psi;
            if (scRetardPerPsi === undefined) scRetardPerPsi = 0.4; // Legacy fallback
            
            base.timing -= scPsi * scRetardPerPsi;

            // 2. VE Boost Filling Gain (Parameter driven)
            let scVeGainPerPsi = sc.ve_per_psi;
            if (scVeGainPerPsi === undefined) scVeGainPerPsi = 0.003; // Legacy fallback
            base.ve *= (1 + scPsi * scVeGainPerPsi);

            // 3. AFR Enrichment
            let scAfrAdderPerPsi = (sc.afr_adder_per_psi !== undefined ? sc.afr_adder_per_psi : 0.1) * octaneMultiplier;
            
            base.afr -= (scPsi * scAfrAdderPerPsi);
        }

        // Trigger calibration error: physical spark angle shifts by the difference between
        // the engine's hardware base offset and the ECU-configured offset.
        // ecuCommandedTiming is what the ECU believes it's firing — the sidebar shows this.
        // actualTiming carries the physical error forward into torque/knock calculations.
        const triggerError = advancedMode
            ? (window.engineTriggerBaseOffset ?? 0) - (this.ecu.triggerAngleOffset ?? 0)
            : 0;
        const ecuCommandedTiming = actualTiming;
        if (triggerError !== 0) actualTiming += triggerError;

        const absTriggerError = Math.abs(triggerError);

        // Effective trigger error accounts for ignition mode periodicity:
        // wasted_spark fires every 360° crank so 360° off = 0° off;
        // distributor fires every (720/cylinders)° so any multiple of that is valid.
        // sequential_cop needs exact cam-phase sync — no symmetry.
        let effectiveTrigErr = absTriggerError;
        if (advancedMode) {
            if (this.ecu.ignitionMode === 'wasted_spark' || this.ecu.ignitionMode === 'wasted_spark_individual') {
                const m = absTriggerError % 360;
                effectiveTrigErr = Math.min(m, 360 - m);
            } else if (this.ecu.ignitionMode === 'distributor') {
                const firingInterval = 720 / (this.engine.global_specs.cylinders || 4);
                const m = absTriggerError % firingInterval;
                effectiveTrigErr = Math.min(m, firingInterval - m);
            }
        }

        const triggerTimingTooFar    = advancedMode && effectiveTrigErr >= 60;
        // Pop chance scales linearly from 10% at 60° to 100% at 300° effective error
        const triggerPopChancePerRev = triggerTimingTooFar
            ? Math.min(1.0, 0.10 + (effectiveTrigErr - 60) / 240 * 0.90)
            : 0;

        const AIR_DENSITY_G_CC = 0.001184;
        const FUEL_DENSITY_G_CC = 0.74;

        const cylVolumeCc = this.engine.global_specs.displacement_cc / this.engine.global_specs.cylinders;
        
        let veVacuumDrop = 0.4 + 0.6 * (Math.min(mapKpa, baroKpa) / baroKpa);
        let physicalVe = base.ve * veVacuumDrop;
        
        // Final actual CFM based on accurate map pressure (includes boost)
        if (!modModifiers.turboProfile) {
            currentCfm = (dispCi * rpm * 0.5 * (physicalVe / 100.0)) / 1728.0;
        }
        // SC cfm note: supercharger airflow is derived from displacement × RPM, not tracked separately here

        // Penalty on airmass if IAT is extreme
        let iatDensityMult = 1.0;
        if (dynamicIat > 120) {
            iatDensityMult = 1.0 - ((dynamicIat - 120) / 1000); // Simple density drop
        }

        // commandedAirmass does NOT use iatDensityMult — the ECU fuels from MAP+VE and doesn't
        // know about temperature-driven density shifts. physicalAirmass does, so cooler/denser
        // air (e.g. intercooler) packs more charge than the ECU commanded → runs lean, as expected.
        let commandedAirmass = cylVolumeCc * AIR_DENSITY_G_CC * (mapKpa / baroKpa) * (commandedVe / 100);
        let physicalAirmass = cylVolumeCc * AIR_DENSITY_G_CC * (mapKpa / baroKpa) * (physicalVe / 100) * iatDensityMult;

        // Flex Fuel Stoich Scaling (Advanced Mode only — eth is already 0 in simple mode)
        const gasStoich = 14.7;
        const ethStoich = 9.0;
        const ethContent = eth; // already 0 in simple mode
        const currentStoich = gasStoich - ((gasStoich - ethStoich) * (ethContent / 100.0));
        const fuelMassMultiplier = gasStoich / currentStoich; // E100 requires ~1.63x more fuel mass

        let ecuDesiredFuelGrams = (commandedAirmass / targetAfr) * fuelMassMultiplier;

        // Closed-Loop Fuel Trim — integrate lambda error from previous frame, apply as fuel multiplier
        if (this.ecu.closedLoopEnabled && !isCranking && !isFloodClear) {
            this._updateStft(rpm, tps, this._prevActualAfr, targetAfr, dt);
            ecuDesiredFuelGrams *= (1 + this.stft);
        }

        // Fuel pressure model — injector flow scales with sqrt(pressure differential)
        // baseFuelPressureKpa is the operating differential (gauge) pressure.
        // injectorReferencePressureKpa is the differential at which the injector's cc/min rating was measured.
        let injectorFlowMultiplier = 1.0;
        if (advancedMode && this.ecu.fuelPressureModelEnabled) {
            const refKpa  = this.ecu.injectorReferencePressureKpa || 300;
            const baseKpa = this.ecu.baseFuelPressureKpa;
            if (this.ecu.fuelSystemType === 'return') {
                // MAP-referenced regulator: rail rises 1:1 with boost — differential is always baseKpa
                const boostAboveBaro = Math.max(0, mapKpa - baroKpa);
                this.ecu.effectiveFuelPressureKpa = this.ecu.baseFuelPressureKpa + boostAboveBaro;
                injectorFlowMultiplier = Math.sqrt(Math.max(0.01, baseKpa) / refKpa);
            } else {
                // Returnless: fixed absolute rail pressure — differential = rail − MAP.
                // Vacuum raises differential (more flow); boost lowers it (less flow).
                this.ecu.effectiveFuelPressureKpa = this.ecu.baseFuelPressureKpa;
                const actualDiffKpa = Math.max(0.5, baseKpa + baroKpa - mapKpa);
                injectorFlowMultiplier = Math.sqrt(actualDiffKpa / refKpa);
            }
        }

        const ecuSizeCc = this.ecu.injectorSizeCc;
        const hwSizeCc  = this.installedInjectorSizeCc ?? ecuSizeCc;

        // ECU calculates IPW from its nominal injector calibration — no pressure awareness
        // Hardware actually delivers at pressure-adjusted flow rate
        let ecuFlowGramsPerMs = ecuSizeCc * FUEL_DENSITY_G_CC / 60000;
        let hwFlowGramsPerMs  = hwSizeCc  * FUEL_DENSITY_G_CC * injectorFlowMultiplier / 60000;

        let baseIpwMs = ecuDesiredFuelGrams / Math.max(ecuFlowGramsPerMs, 0.00001);
        const activeDeadTimeMs = this.ecu.injectorDeadTimeMs;
        let ipwMs = baseIpwMs + activeDeadTimeMs;

        // ACCEL MS ADDER — extra injection time during acceleration enrichment event
        if (advancedMode && this.ecu.accelMode === 'ms_adder' && this.accelCurrentMs > 0 && !isCranking) {
            ipwMs += this.accelCurrentMs;
        }

        // Flood clear: WOT cranking purges raw fuel — disable injection
        if (isFloodClear) {
            ipwMs = 0;
        }

        let cycleTimeMs = rpm > 0 ? (120000 / rpm) : 0;
        let injectorDutyCycle = cycleTimeMs > 0 ? (ipwMs / cycleTimeMs) * 100 : 0;

        // Simulate static injectors (Duty Cycle > 100%)
        if (cycleTimeMs > 0 && ipwMs > cycleTimeMs) {
            ipwMs = cycleTimeMs; 
        }

        const voltage = Math.max(this.ecu.batteryVoltage ?? 14, 6);
        const nominalDeadtimeMs = this.installedInjectorDtMs ?? (this.engine.global_specs.true_deadtime_ms || 0.70);
        const trueDeadtimeMs = nominalDeadtimeMs * Math.pow(14 / voltage, 1.5);
        let effectiveFlowMs = Math.max(0, ipwMs - trueDeadtimeMs);
        let actualFuelGrams = effectiveFlowMs * hwFlowGramsPerMs;

        let physicalAfr = physicalAirmass / Math.max(actualFuelGrams, 0.00001);
        let actualAfr = physicalAfr * (14.7 / currentStoich); // Display and logic on Gas Scale

        // Pump starvation — lean out if injector demand exceeds pump capacity
        if (advancedMode && this.ecu.fuelPressureModelEnabled) {
            const activePumpLph = this.activePumpLph ?? this.ecu.stockFuelPumpLph;
            const pumpCapacityCcPerMin = activePumpLph * 1000 / 60;
            const numCylinders = this.engine.global_specs?.cylinders ?? 4;
            const demandCcPerMin = hwSizeCc * injectorFlowMultiplier
                                   * numCylinders * (injectorDutyCycle / 100);
            this.ecu.fuelPumpDemandPct = pumpCapacityCcPerMin > 0
                ? (demandCcPerMin / pumpCapacityCcPerMin) * 100 : 0;
            if (demandCcPerMin > pumpCapacityCcPerMin && pumpCapacityCcPerMin > 0) {
                const starvationFactor = pumpCapacityCcPerMin / demandCcPerMin;
                actualFuelGrams *= starvationFactor;
                physicalAfr = physicalAirmass / Math.max(actualFuelGrams, 0.00001);
                actualAfr   = physicalAfr * (14.7 / currentStoich);
            }
        }

        // Combustion variance noise — applied to actualAfr before any downstream logic
        if (advancedMode && this.ecu.o2DelayEnabled && !isCranking) {
            if (Math.random() < 0.60 * dt) {
                this.afrNoiseTarget = (Math.random() * 2 - 1) * 0.02; // ±2%
            }
            this.afrNoiseCurrent += (this.afrNoiseTarget - this.afrNoiseCurrent)
                                    * Math.min(1, dt / 0.15);
            const noiseFrac = rpm < 3000 ? 1
                : rpm >= 3500 ? 0
                : (3500 - rpm) / 500;
            actualAfr   *= (1 + this.afrNoiseCurrent * noiseFrac);
            physicalAfr  = actualAfr * (currentStoich / 14.7);
        }

        let isOverrun = false;

        const _decelDutyPct = Interpolation.interpolate1D((typeof currentCoolantC !== 'undefined' && currentCoolantC !== null) ? currentCoolantC : 90, this.ecu.idleDutyCoolantAxis, this.ecu.idleDutyCurve);
        const _decelIdleDuty = (_decelDutyPct / 100) * (this.ecu.maxIdleTpsPct ?? 10);
        if (advancedMode && this.ecu.decelCutEnabled && tps <= _decelIdleDuty + 0.5 && rpm > (this.ecu.liveIdleTargetRpm ?? 850) + 400) {
            this.overrunTimer += dt;
            if (this.overrunTimer >= this.ecu.decelCutDelay) {
                isOverrun = true;
                actualAfr = 22.0; 
                ipwMs = 0; 
            }
        } else {
            this.overrunTimer = 0;
        }
        
        const ignDiff = actualTiming - base.timing;
        let timEff = 1.0 - (Math.pow(Math.min(1, Math.abs(ignDiff) / 25), 1.2) * 0.9);
        
        let afrEff = 0;
        if (isOverrun) {
            afrEff = 0; // finalTq zeroed below after calculation
        } else {
            // New dynamic AFR efficiency relative to the engine's Ideal AFR (base.afr)
            let afrError = actualAfr - base.afr;
            if (afrError > 0) { // Lean
                if (afrError <= 1.2) afrEff = 1.0 - (afrError / 1.2) * 0.05; // Slightly lean: minor loss
                else if (afrError <= 2.5) afrEff = 0.95 - ((afrError - 1.2) / 1.3) * 0.25; // Lean: 0.95 -> 0.70
                else if (afrError <= 4.5) afrEff = 0.70 - ((afrError - 2.5) / 2.0) * 0.60; // Very lean: 0.70 -> 0.10
                else afrEff = 0.1;
            } else { // Rich (afrError is negative)
                let absError = Math.abs(afrError);
                if (absError <= 1.0) afrEff = 1.0; // Sweet spot: no loss for minor enrichment
                else if (absError <= 4.5) afrEff = 1.0 - (absError - 1.0) / 3.5 * 0.9; // Rich: 1.0 -> 0.1
                else afrEff = 0.1;
            }
        }
        
        let totalEff = Math.max(0, Math.min(1.2, (timEff * 0.50) + (afrEff * 0.50)));

        // Coil dwell no-fire threshold — scales with plug gap and boost pressure
        // Wider gap needs more coil energy; higher boost compresses effective gap
        const engineMinDwellMs = this.engine.global_specs?.min_coil_dwell_ms ?? 2.0;
        const REF_GAP_IN       = 0.040;
        const MIN_GAP_IN       = 0.015; // floor prevents nonsensical values at extreme boost
        const boostPsiForDwell = Math.max(0, (mapKpa - baroKpa) / 6.89476);
        const effectiveGapIn   = Math.max(MIN_GAP_IN, this.ecu.sparkPlugGapIn - (boostPsiForDwell / 10) * 0.005);
        const DWELL_MIN_MS     = engineMinDwellMs * (effectiveGapIn / REF_GAP_IN);
        const dwellEff         = (!advancedMode || this.ecu.coilDwellMs >= DWELL_MIN_MS) ? 1.0 : 0.0; // hard no-fire cutoff (bypassed in Normal Mode)

        // Compressor efficiency bonus: a well-matched turbo in its efficiency island delivers
        // denser, cooler air mass beyond what boost pressure alone captures.
        // Linear: 0% bonus at ≤55% efficiency → +40% bonus at 90% efficiency.
        let compEffBonusMult = 1.0;
        if (modModifiers.turboProfile && this.actualBoostKpa > 0) {
            const effBonus = Math.max(0, Math.min(1, (turboEff - 55) / 35)) * 0.40;
            compEffBonusMult = 1.0 + effBonus;
        }

        let torqueMapKpa = mapKpa;
        if (modModifiers.turboProfile) {
            torqueMapKpa -= this.actualBoostKpa; 
        }
        if (modModifiers.scProfile && (modModifiers.scProfile.sc_type === 'roots' || modModifiers.scProfile.sc_type === 'twin-screw') && modModifiers.scProfile.sc_displacement_l) {
            torqueMapKpa -= this.scBoostKpa;
        }
        torqueMapKpa = Math.max(20, torqueMapKpa);

        let finalTq = base.torque * totalEff * (torqueMapKpa / baroKpa) * veVacuumDrop * iatDensityMult * dwellEff * compEffBonusMult;

        // Compression ratio power effect: ~3% per CR point relative to stock
        if (modModifiers.cr_adder !== 0) {
            finalTq *= 1 + modModifiers.cr_adder * 0.03;
        }

        // Disabled cylinders (coil melt) reduce torque proportionally
        const totalCyls   = this.engine.global_specs.cylinders;
        const disabledCnt = this.ecu.disabledCylinders.size;
        if (disabledCnt > 0 && totalCyls > 0) {
            finalTq *= (totalCyls - disabledCnt) / totalCyls;
        }

        // Nitrous torque adder — applied after base efficiency scaling, before failure checks.
        // Extra combustion energy from oxidizer bypass normal VE/airflow scaling, but can still
        // trigger the misfire/failure model below (oversized jet on low octane = boom).
        if (nitrousTorqueAdder > 0) finalTq += nitrousTorqueAdder;

        // Smart alternator torque bonus: lower voltage = less alternator drag = more power
        // 0% bonus at 14.5V, +3% at 12.5V
        if (this.ecu.smartAltEnabled) {
            const altBonus = Math.max(0, Math.min(0.03, (14.5 - this.ecu.currentBatteryV) / 2.0 * 0.03));
            finalTq *= (1 + altBonus);
        }

        // No combustion torque on decel fuel cut
        if (isOverrun) finalTq = 0;

        // Continuous Minor Noise injection
        if (!isOverrun && rpm > 0) {
            let noise = (Math.random() * 4) - 2;
            finalTq += noise;
        }

        let isJitter = false;
        let isSevere = false;
        let isPopping = false;
        let popIntensity = 0;
        
        // --- REV LIMITER AND LAUNCH CONTROL CUT LOGIC ---
        let isRevLimit = false;
        let isLcLimit = isLaunchLimitActive;
        let revLimitFuelActive = false;
        let revLimitSparkActive = false;

        if (rpm >= this.ecu.revLimit) {
            this.revLimitActive = true;
        } else if (rpm <= (this.ecu.revLimit - (this.ecu.revLimitHysteresis || 200))) {
            this.revLimitActive = false;
        }

        if (this.revLimitActive || isLcLimit) {
            isRevLimit = true;
            
            if (isLcLimit) {
                // Launch Control Cut Type
                if (launchCutType === "Fuel") {
                    revLimitFuelActive = true;
                } else if (launchCutType === "Ignition") {
                    revLimitSparkActive = true;
                } else if (launchCutType === "Both") {
                    revLimitFuelActive = true;
                    revLimitSparkActive = true;
                }
            } else {
                // Standard Rev Limiter
                if (this.ecu.revLimitFuelCut) revLimitFuelActive = true;
                if (this.ecu.revLimitSparkCut) revLimitSparkActive = true;
            }
        }

        // --- BOOST CUT TORQUE OVERRIDE ---
        if (this.boostCutActive) {
            finalTq = 0;
            revLimitSparkActive = true; // Simulates ignition cut
            isPopping = true;
            popIntensity = 0.8 + Math.random() * 0.2;
        }

        if (isRevLimit) {
            if (revLimitFuelActive) {
                actualAfr = 22.0;
                finalTq = 0;
            }
            if (revLimitSparkActive) {
                finalTq = 0;
                
                if (!revLimitFuelActive) {
                    isPopping = true;
                    // LC Pop logic: from +10 BTDC downwards
                    if (isLcLimit) {
                        // popIntensity scales from 0.4 at 10 deg to 1.0 at -20 deg
                        // formula: 0.4 + clamp((10 - actualTiming) / 30, 0, 1) * 0.6
                        popIntensity = 0.4 + Math.max(0, Math.min(1.0, (10 - actualTiming) / 30.0)) * 0.6;
                    } else {
                        popIntensity = 0.8 + Math.random() * 0.2;
                    }
                }
            }
        }

        // Force IPW to 0 if fuel cut is active (Limiter or Overrun)
        if (revLimitFuelActive || isOverrun) {
            ipwMs = 0;
        }

        if (!isCranking && !isOverrun && !isRevLimit && tps < 15 && rpm > 1500) {
            if (actualAfr > 15.5 && actualAfr < 17.0 && actualTiming < 0) {
                popIntensity = Math.min(1.0, Math.abs(actualTiming) / 40.0);
                let popChance = 0.56 - (popIntensity * 0.51);
                if (Math.random() < popChance) {
                    isPopping = true;
                    finalTq *= 0.1;
                }
            }
        }

        // Flames mode: timing worse than -40° ATDC + extreme rich AFR (<10.5) on decel
        // Unburned fuel dumps into the exhaust and ignites — NES flamethrower sound + 30% instant failure risk per pop.
        let isFlamesMode = false;
        if (!isCranking && !isOverrun && !isRevLimit && tps < 15 && rpm > 1500
                && actualTiming < -40 && actualAfr < 10.5) {
            // ~5-10% chance per frame — averages roughly one flames event per second
            const flamesPopChance = 0.05 + ((10.5 - Math.max(0, actualAfr)) / 10.5) * 0.05;
            if (Math.random() < flamesPopChance) {
                isFlamesMode = true;
                isPopping = true;
                popIntensity = 0.85 + Math.random() * 0.15;
                finalTq *= 0.05; // near-zero torque during exhaust combustion event
                if (Math.random() < 0.15) {
                    this._blowEngine("Exhaust fire — extreme rich mixture + retarded timing.");
                    return { error: "Catastrophic Failure: Engine destroyed by exhaust fire (extreme rich mixture + retarded timing).", valid: false };
                }
            }
        }

        // Trigger timing too far: pop once per crank revolution, probability and intensity scale with error
        if (triggerTimingTooFar && !isPopping) {
            this.triggerPopRevAccum += (rpm / 60) * 360 * dt;
            if (this.triggerPopRevAccum >= 360) {
                this.triggerPopRevAccum -= 360;
                if (Math.random() < triggerPopChancePerRev) {
                    isPopping    = true;
                    // Intensity: 0.2–0.4 at 60°, 0.6–0.8 at 300°+ effective error
                    const _trigErrFrac = Math.min(1.0, (effectiveTrigErr - 60) / 240);
                    popIntensity = 0.2 + _trigErrFrac * 0.4 + Math.random() * 0.2;
                }
            }
        } else if (!triggerTimingTooFar) {
            this.triggerPopRevAccum = 0;
        }

        // Sequential exhaust-stroke backfire: 340-360° raw offset = ECU firing on exhaust stroke every rev.
        // Only meaningful for sequential_cop (wasted_spark/distributor are unaffected at this range).
        if (isCranking && absTriggerError >= 340 && this.ecu.ignitionMode === 'sequential_cop' && !isPopping) {
            this.seqBackfireRevAccum = (this.seqBackfireRevAccum || 0) + (rpm / 60) * 360 * dt;
            if (this.seqBackfireRevAccum >= 360) {
                this.seqBackfireRevAccum -= 360;
                isPopping    = true;
                popIntensity = 0.75 + Math.random() * 0.25;  // loud exhaust bang
            }
        } else if (!isCranking || absTriggerError < 340) {
            this.seqBackfireRevAccum = 0;
        }

        if (!isRevLimit && rpm < 2500 && tps < 15 && !isOverrun && !isPopping) {
            let jitterProgress = 0;

            if (actualAfr > 15.8) {
                if (actualAfr <= 16.5) { isJitter = true; jitterProgress = (actualAfr - 15.8) / (16.5 - 15.8); } 
                else { isSevere = true; }
            } else if (actualAfr < 12.3) {
                if (actualAfr >= 11.5) { isJitter = true; jitterProgress = (12.3 - actualAfr) / (12.3 - 11.5); } 
                else { isSevere = true; }
            }

            if (isJitter || isSevere) {
                const baseCache = this.mapCache["Base"] || this.mapCache["Low Cam Profile"];
                const refTorque500 = baseCache && baseCache.tqs.length > 0 ? baseCache.tqs[0] : 50;
                
                if (isJitter) {
                    let minJitter = refTorque500 * 0.05;
                    let maxJitter = refTorque500 * 0.20;
                    let jitterAmp = minJitter + ((maxJitter - minJitter) * jitterProgress); 
                    finalTq -= (Math.random() * jitterAmp);
                } else if (isSevere) {
                    let severeDev = actualAfr > 16.5 ? (actualAfr - 16.5) : (11.5 - actualAfr);
                    let severeProgress = Math.min(severeDev / 0.5, 1.0); 
                    let surgeIntensity = 0.20 + (severeProgress * 0.75); 
                    
                    if (Math.random() < surgeIntensity) {
                        let powerLoss = 0.50 + (severeProgress * 0.50);
                        finalTq *= (1.0 - (Math.random() * powerLoss));
                        if (Math.random() > 0.4) {
                            let maxBucking = refTorque500 * (0.50 + (severeProgress * 1.0));
                            finalTq -= (Math.random() * maxBucking); 
                        }
                    }
                }
            }
        }

        // --- NEW MISFIRE AND CATASTROPHIC FAILURE LOGIC ---
        const maxSafeRpm = this.engine.mechanical_limits.max_safe_rpm + (modModifiers.rpm_shift || 0);
        const maxTqLimit = this.engine.mechanical_limits.max_cylinder_pressure_tq + modModifiers.tq_capacity_adder;
        const cr = (this.engine.global_specs.compression_ratio || 9.0) + modModifiers.cr_adder;

        let failureChance = 0;

        // --- IGNITION DWELL & CROSSFIRE (distributor only — separate from detonation knock) ---
        let ignitionMisfireChance = 0;
        const cylCountIgn = this.engine.global_specs.cylinders;
        const availableRechargeMs = this.ecu.getAvailableRechargeMs(rpm, cylCountIgn);

        if (this.ecu.ignitionMode === "distributor") {
            // Single coil must recharge between every cylinder — misfire if dwell exceeds available time
            if (this.ecu.coilDwellMs > availableRechargeMs) {
                const overage = this.ecu.coilDwellMs - availableRechargeMs;
                ignitionMisfireChance += Math.min(1.0, overage / availableRechargeMs);
            }
            // Cap crossfire: at very high RPM the rotor sweeps past adjacent terminals before they insulate
            const rotorDwellMs = (720 / cylCountIgn) / (rpm / 60 * 360) * 1000;
            if (rotorDwellMs < 0.5) {
                ignitionMisfireChance += Math.min(0.4, (0.5 - rotorDwellMs) / 0.5 * 0.4);
            }
        }

        // Dwell/crossfire misfire event — torque cut + pop, but not a detonation knock event
        if (ignitionMisfireChance > 0 && Math.random() < ignitionMisfireChance) {
            finalTq *= (0.1 + Math.random() * 0.3);
            isPopping = true;
        }

        // 1. RPM Failure Logic (up to 15% over max safe)
        const absoluteMaxRpm = maxSafeRpm * 1.15;
        if (rpm > absoluteMaxRpm) {
            this._blowEngine(`Valve float / rod failure — severe over-rev (${Math.round(rpm)} RPM, limit ${Math.round(maxSafeRpm)} RPM).`);
            return { error: "Catastrophic Failure: Valve Float / Rod Failure from severe over-rev.", valid: false };
        } else if (rpm > maxSafeRpm) {
            const rpmOveragePct = (rpm - maxSafeRpm) / (maxSafeRpm * 0.15);
            failureChance += Math.pow(rpmOveragePct, 2) * 0.05;
        }

        // --- KNOCK STRESS MODEL & CYLINDER PRESSURE LOGIC (only over 40% TPS) ---
        let totalStress = 0;
        let knockChance = 0;

        if (tps >= 40) {
            // Design CR (stock) vs actual CR (after piston mods).
            // Build penalty is zero for stock builds; grows quadratically as pistons raise CR above factory spec.
            const designCr = this.engine.global_specs.compression_ratio || 9.0;
            const actualCr = cr; // cr already = designCr + modModifiers.cr_adder

            // A. Octane stress (deficiency) and octane resistance (surplus).
            // Deficiency: running below required octane raises stress linearly.
            // Surplus: running above required octane reduces non-IAT stress — higher octane raises
            // the autoignition threshold, allowing more boost/timing before knock. IAT is excluded
            // because heat-induced detonation is a physical temperature problem octane can't fully solve.
            // Scale: ~10% reduction at +4 pts surplus (91 on 87 engine), ~72% at +29 pts (116 on 87 engine).
            const octaneStress    = Math.max(0, requiredOctane - octane) * 12.0;
            const octaneSurplus   = Math.max(0, octane - requiredOctane);
            const octaneResist    = Math.min(0.75, octaneSurplus * 0.025);

            // B. Compression stress (design-limit penalty + tuner build penalty)
            // Timing retard below MBT reduces peak cylinder pressure, relieving CR-induced stress —
            // this is the primary way to survive an extreme-CR build (e.g. big piston mod).
            const designPenalty    = Math.max(0, designCr - 9.0) * 3.5;
            const buildPenalty     = Math.pow(Math.max(0, actualCr - designCr), 2.0) * 2.0;
            const retardDeg        = Math.max(0, -ignDiff); // degrees timing is BELOW MBT
            const crPressureRelief = Math.min(0.80, retardDeg * 0.09); // caps at 80% relief at ~9° retarded
            const crRelief         = Math.min(10, Math.max(0, designCr - actualCr) * 2.5);
            const crStress         = Math.max(0, (designPenalty + buildPenalty) * (1 - crPressureRelief) - crRelief);

            // C. Boost stress — relative to a CR-adjusted safe boost ceiling.
            // High CR shrinks the safe ceiling; low CR (turbo dish pistons) raises it.
            // The original single formula breaks below CR ~7.2 (denominator goes negative).
            const boostPsi      = Math.max(0, (mapKpa - baroKpa) / 6.89476);
            const crDelta       = actualCr - 9.0;
            const dynamicScaler = crDelta >= 0
                ? 5.93 / (1.0 + crDelta * 0.46)           // high CR → lower safe boost ceiling
                : 5.93 * (1.0 + Math.abs(crDelta) * 0.35); // low CR  → higher safe boost ceiling
            const boostStress   = Math.pow(boostPsi / Math.max(0.1, dynamicScaler), 2.5);

            // D. Ignition & LSPI stress
            // ignDiff = actualTiming - base.timing; positive means the table is over-advanced vs MBT
            const overAdvance    = Math.max(0, ignDiff);
            const ignitionStress = (overAdvance * 2.0) + Math.pow(Math.max(0, overAdvance - 2.0), 2.0);
            // LSPI: high boost at low RPM creates extreme cylinder pressure spikes
            const lspiStress     = (Math.max(0, 3000 - rpm) / 2000) * (boostPsi / 10.0) * 50.0;

            // E. Nitrous stress
            const n2oHp        = (nitrousActive && this.ecu.nitrousEnabled) ? (this.ecu.nitrousCalculatedHp ?? 0) : 0;
            const nitrousStress = (n2oHp / 10.0) * 1.3;

            // Environmental multipliers (IAT heat amplifies non-IAT stresses; lean AFR amplifies everything)
            const thermalScale = dynamicIat <= 150 ? 1.0
                : 1.0 + ((dynamicIat - 150) / 50) * 1.0; // 1.0 → 2.0 from 150°F to 200°F
            const leanDelta    = Math.max(0, actualAfr - base.afr);
            const afrScale     = leanDelta < 1.1 ? 1.0
                : 1.0 + ((leanDelta - 1.1) / 0.4) * 2.2; // 1.0 → 3.2 from Δ1.1 to Δ1.5

            // IAT direct stress: hot charge temps cause detonation even on otherwise-perfect tunes.
            // Kept separate from thermalScale so IAT doesn't compound with itself.
            const iatStress  = Math.max(0, (dynamicIat - 150) / 50) * 43.0; // 0 → 43% from 150°F to 200°F

            const nonIatBase = octaneStress + crStress + boostStress + ignitionStress + lspiStress + nitrousStress;
            totalStress      = Math.min(150, (nonIatBase * (1 - octaneResist) * thermalScale + iatStress) * afrScale);

            // Knock chance: ~0.5/s at stress=26 (mild ping), ~4/s at stress=100 (constant detonation)
            knockChance = 0.001 + 4.0 * Math.pow(Math.min(totalStress, 100) / 100, 1.2);

            // Cylinder pressure mechanical failure (separate from detonation — blocks and rods, not rings)
            if (finalTq > maxTqLimit) {
                const tqOveragePct     = (finalTq - maxTqLimit) / (maxTqLimit * 0.60);
                const boundedTqOverage = Math.min(1, Math.max(0, tqOveragePct));
                failureChance += 0.01 + (0.19 * boundedTqOverage);
                if (tqOveragePct > 1.2) {
                    failureChance += 0.5; // instant death way over block limit
                }
            }

            // High-stress detonation amplifies mechanical failure risk quadratically under load
            if (knockChance > 0) {
                const torqueStressRatio = finalTq / maxTqLimit;
                failureChance += knockChance * Math.pow(Math.max(0, torqueStressRatio), 2) * 0.20 * dt;
            }
        }

        // --- KNOCK EVENT — 4-tier severity system driven by totalStress ---
        // torque-only: invisible to sensor, faint spectrogram blip, small torque dip
        // quiet:       faint pop, near sensor threshold, low failure risk
        // normal:      audible knock, above threshold, moderate failure risk
        // loud:        hard detonation, well above threshold, high failure risk
        //
        // Cascade: a knock event destabilizes combustion for the next few frames.
        // The tier one step below the firing tier gets 2× weight on each subsequent
        // frame, cascading downward (loud→normal→quiet→torque-only) for up to 3 frames.

        // Capture which tier gets the cascade boost this frame, then advance state.
        const cascadeBoostTier = this.knockCascadeFrames > 0 ? this.knockCascadeNextTier : null;
        if (this.knockCascadeFrames > 0) {
            this.knockCascadeFrames--;
            const _cascadeNext = { normal: 'quiet', quiet: 'torque-only', 'torque-only': null };
            this.knockCascadeNextTier = this.knockCascadeFrames > 0
                ? (_cascadeNext[cascadeBoostTier] ?? null)
                : null;
        }

        let knockTier = null;
        if (knockChance > 0 && Math.random() < knockChance * dt) {
            isPopping = true;
            this.knockCount++;

            const stressSeverity = Math.min(1, totalStress / 100);

            // Tier weights — use weighted random so cascade boost doubles one tier's share.
            // torque-only weight is the remainder (1 - loud - normal - quiet).
            let wLoud   = Math.max(0, stressSeverity - 0.35) * 0.65; // 0% until stress~35, then → 42%
            let wNormal = 0.10 + stressSeverity * 0.30;              // 10% → 40%
            let wQuiet  = 0.55 - stressSeverity * 0.30;              // 55% → 25%
            let wTorque = Math.max(0.01, 1 - wLoud - wNormal - wQuiet); // ~35% at low, ~5% at high

            // Apply cascade 2× weight to the boosted tier this frame.
            if      (cascadeBoostTier === 'normal')      wNormal *= 2;
            else if (cascadeBoostTier === 'quiet')       wQuiet  *= 2;
            else if (cascadeBoostTier === 'torque-only') wTorque *= 2;

            // Weighted roll — no need to normalise, just roll against total weight.
            const wTotal    = wLoud + wNormal + wQuiet + wTorque;
            const tierRoll  = Math.random() * wTotal;

            if (tierRoll < wLoud) {
                knockTier    = 'loud';
                popIntensity = 0.7 + Math.random() * 0.3;
                finalTq     *= (0.30 + Math.random() * 0.30); // 30–60% remaining torque
                const failChance = 0.02 + stressSeverity * 0.45;
                if (Math.random() < failChance) {
                    this._blowEngine(`Detonation — LOUD knock (stress=${totalStress.toFixed(1)}, severity=${(stressSeverity*100).toFixed(1)}%, failChance=${(failChance*100).toFixed(1)}%).`);
                    return { error: "Catastrophic Failure: Engine Destroyed by detonation.", valid: false };
                }
            } else if (tierRoll < wLoud + wNormal) {
                knockTier    = 'normal';
                popIntensity = 0.35 + Math.random() * 0.25;
                finalTq     *= (0.60 + Math.random() * 0.20); // 60–80% remaining torque
                const failChance = 0.001 + stressSeverity * 0.02;
                if (Math.random() < failChance) {
                    this._blowEngine(`Detonation — NORMAL knock (stress=${totalStress.toFixed(1)}, severity=${(stressSeverity*100).toFixed(1)}%, failChance=${(failChance*100).toFixed(1)}%).`);
                    return { error: "Catastrophic Failure: Engine Destroyed by detonation.", valid: false };
                }
            } else if (tierRoll < wLoud + wNormal + wQuiet) {
                knockTier    = 'quiet';
                popIntensity = 0.05 + Math.random() * 0.15;
                finalTq     *= (0.80 + Math.random() * 0.10); // 80–90% remaining torque
                const failChance = stressSeverity * 0.001;
                if (Math.random() < failChance) {
                    this._blowEngine(`Detonation — QUIET knock (stress=${totalStress.toFixed(1)}, severity=${(stressSeverity*100).toFixed(1)}%, failChance=${(failChance*100).toFixed(1)}%).`);
                    return { error: "Catastrophic Failure: Engine Destroyed by detonation.", valid: false };
                }
            } else {
                knockTier    = 'torque-only';
                popIntensity = 0;
                finalTq     *= (0.94 + Math.random() * 0.02); // ~5% torque dip, sensor-invisible
            }

            // Arm cascade for subsequent frames (engine survived, combustion is destabilized).
            // LOUD → 3 frames (normal, quiet, torque-only)
            // NORMAL → 2 frames (quiet, torque-only)
            // QUIET → 1 frame  (torque-only)
            // TORQUE-ONLY → no cascade
            const _cascadeStart = { loud: ['normal', 3], normal: ['quiet', 2], quiet: ['torque-only', 1], 'torque-only': [null, 0] };
            const [nextCTier, nextCFrames] = _cascadeStart[knockTier];
            if (nextCFrames > 0) {
                this.knockCascadeNextTier = nextCTier;
                this.knockCascadeFrames   = nextCFrames;
            }

            if (!window.location.pathname.includes('tests')) {
                const tierLabels = { loud: '🔴 LOUD', normal: '🟠 NORMAL', quiet: '🟡 QUIET', 'torque-only': '⚪ TORQUE-ONLY' };
                const cascadeTag = cascadeBoostTier ? ` [cascade↑${cascadeBoostTier}]` : '';
                console.log(`[KNOCK] ${tierLabels[knockTier]}${cascadeTag} | stress=${totalStress.toFixed(1)} severity=${(stressSeverity*100).toFixed(1)}% | rpm=${Math.round(rpm)} tps=${tps.toFixed(1)}%`);
            }
        }

        if (failureChance >= 0.001 && Math.random() < failureChance) {
            this._blowEngine(`Extreme knock / cylinder pressure limit exceeded (failureChance=${(failureChance*100).toFixed(2)}%, stress=${totalStress.toFixed(1)}, tq=${finalTq.toFixed(1)} lb-ft).`);
            return { error: "Catastrophic Failure: Engine Destroyed due to extreme knock or cylinder pressure limit.", valid: false };
        }

        const isIdling = (rpm < (this.ecu.liveIdleTargetRpm ?? 850) + (this.ecu.idleRpmUpperLimit ?? 200) && tps <= _decelIdleDuty + 5);

        if (modModifiers.turboProfile && isIdling) {
            finalTq *= 0.95; // 5% exhaust backpressure penalty at idle
        }
        if (turboHpGain > 0 && activeBoostPsi >= 0.5 && rpm > 0 && !isIdling) {
            finalTq += (turboHpGain * totalEff * iatDensityMult * 5252) / rpm;
        }

        if (scHpGain !== 0 && rpm > 0) {
            finalTq += (scHpGain * totalEff * iatDensityMult * 5252) / rpm;
        }

        finalTq = Math.max(0, finalTq);

        this.lastTorque = finalTq; // used next frame as turbine driving force

        // Trigger timing too far: kill torque; stall timer runs only while not cranking
        if (triggerTimingTooFar) {
            finalTq = 0;
            if (!isCranking) {
                this.triggerTimingStallTimer += dt;
            }
        } else {
            this.triggerTimingStallTimer = 0;
        }
        if (this.triggerTimingStallTimer > 1.5) {
            return {
                error:   "Engine stalled (trigger angle misconfigured — check Setup > Trigger Calibration)",
                stalled: true,
                valid:   false
            };
        }

        if (isCranking || isRevLimit || !isIdling) {
            this.stallTimer = 0;
        } else if (!isOverrun && (actualAfr >= 17.0 || actualAfr <= 9.8)) {
            this.stallTimer += dt;
            finalTq *= 0.1;
        } else if (!isOverrun && actualAfr < 11.0) {
            let richFactor = (11.0 - actualAfr) / (11.0 - 9.8); 
            let powerMult = 1.0 - Math.pow(richFactor, 1.5) * 0.9; 
            finalTq *= powerMult;
            this.stallTimer = Math.max(0, this.stallTimer - (dt*2));
        } else {
            this.stallTimer = Math.max(0, this.stallTimer - (dt*2));
        }

        if (this.stallTimer > 5) return { error: "Engine stalled (too lean/rich)", stalled: true, valid: false };

        this._prevActualAfr = actualAfr;

        return {
            valid: true, rpm: rpm, mapKpa: mapKpa, actualMapKpa: actualMapKpa, torque: finalTq,
            actualAfr: actualAfr, physicalAfr: physicalAfr, timing: ecuCommandedTiming,
            overrun: isOverrun, revLimit: isRevLimit, sparkCut: revLimitSparkActive, ipw: ipwMs, popping: isPopping, popIntensity: popIntensity, knockTier: knockTier, totalStress: totalStress, knockChance: knockChance, flamesMode: isFlamesMode,
            vvtIntake: this.actualVvtIntake, vvtIntakeTarget: vvtTargetIn,
            vvtExhaust: this.actualVvtExhaust, vvtExhaustTarget: vvtTargetEx,
            vvlActive: vvlActive, boostPsi: activeBoostPsi,
            scBoostPsi: this.scBoostKpa / 6.89476,
            scRpm: modModifiers.scProfile ? (rpm * (this.ecu.scPulleyRatio || 2.5)) : 0,
            scMaxRpm: modModifiers.scProfile ? (modModifiers.scProfile.max_sc_rpm || 0) : 0,
            scType: modModifiers.scProfile ? (modModifiers.scProfile.sc_type || '') : '',
            disabledCylinders: this.ecu.disabledCylinders,
            
            commandedAfr: targetAfr,
            wgDuty: this.ecu.getWgDuty(rpm, tps),
            cfm: currentCfm,
            waMassflow: waMassflow,
            compressorPR: compressorPR,
            estimatedWHP: waMassflow * 10,
            turboSpeed: turboSpeed,
            turboEff: turboEff,
            iat: dynamicIat,
            idealBaseTorque: base.torque,
            idealBaseVe: base.ve,
            idealBaseAfr: base.afr,
            idealMbt: base.timing,
            actualTiming: actualTiming,
            modTorqueMult: modModifiers.torque_mult,
            modVeMult: modModifiers.ve_mult,
            misfireChance: knockChance * 100,
            knockCount: this.knockCount,
            commandedVe: commandedVe,
            nitrousActive: nitrousActive,
            boostCutActive: this.boostCutActive,
            scExponent: activeScExponent,
            scChoke: activeScChoke,
            stft: this.stft,
            lopeRpmDelta: lopeRpmDelta
        };
    }
}

let physics = new AlphaXPhysics(sampleEngine, ecu);
